import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { ShoppingCart, Filter, X, Plus, Minus, Trash2, Clock, CreditCard, Settings, MapPin, User, CheckCircle, AlertCircle, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Stripe promise
let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/stripe/publishable-key`);
      if (response.data.success) {
        stripePromise = loadStripe(response.data.publishable_key);
      }
    } catch (error) {
      console.error('Error loading Stripe key:', error);
    }
  }
  return stripePromise;
};

// Stripe Card Input styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': {
        color: '#9CA3AF',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#EF4444',
    },
  },
};

// Payment Modal Component
const PaymentModal = ({ 
  show, 
  onClose, 
  amount, 
  onPaymentSuccess,
  checkoutForm 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Create payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (pmError) {
        setError(pmError.message);
        setProcessing(false);
        return;
      }

      // Call the parent's success handler with payment method
      await onPaymentSuccess(paymentMethod);
      setProcessing(false);
      onClose();
    } catch (err) {
      setError(err.message || 'Payment failed');
      setProcessing(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[rgb(41,173,211)]" />
              Complete Payment
            </h3>
            <p className="text-sm text-gray-400 mt-1">Total Amount: ${amount.toFixed(2)}</p>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            className="text-gray-400 hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handlePayment} className="space-y-4">
          <div className="bg-gray-750 p-4 rounded-lg border border-gray-600">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Card Details *
            </label>
            <div className="bg-gray-700 p-3 rounded border border-gray-600">
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#ffffff',
                      '::placeholder': {
                        color: '#9CA3AF',
                      },
                    },
                    invalid: {
                      color: '#ef4444',
                    },
                  },
                }}
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-900 bg-opacity-20 border border-red-500 rounded-lg p-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing || !stripe}
              className="flex-1 px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-md hover:bg-[rgb(31,163,201)] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4" />
                  <span>Pay ${amount.toFixed(2)}</span>
                </>
              )}
            </button>
          </div>
        </form>

        <div className="mt-4 pt-4 border-t border-gray-700">
          <p className="text-xs text-gray-400 text-center">
            🔒 Secure payment powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

// Checkout Form Component with Stripe
const CheckoutFormContent = ({ 
  checkoutForm, 
  setCheckoutForm, 
  enabledOrderTypes, 
  handlePlaceOrder,
  getCartTotal,
  calculateTax,
  calculateDiscount,
  calculateCouponDiscount,
  calculateShippingFee,
  appliedCoupon,
  cart,
  cardComplete,
  setCardComplete,
  isPlacingOrder,
  storeSettings,
  ecommerceSettings,
  setShowOrderTypeModal,
  deliveryAreas,
  handleCityChange,
  availableZipCodes,
  pickupLocations,
  availablePickupTimes,
  availableDeliveryTimes,
  calculatePreOrderPickupDates,
  calculatePreOrderPickupTimes,
  calculatePreOrderDeliveryDates,
  calculatePreOrderDeliveryTimeRanges,
  showCouponInput,
  setShowCouponInput,
  couponCode,
  setCouponCode,
  couponError,
  applyingCoupon,
  handleApplyCoupon,
  handleRemoveCoupon,
  shippingCountries,
  handleShippingCountryChange,
  availableStates,
  businessInfo,
  selectedTipOption,
  setSelectedTipOption,
  customTipAmount,
  setCustomTipAmount,
  calculateTip
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const isPickupOrder = checkoutForm.orderType === 'orderNowPickup' || checkoutForm.orderType === 'preOrderPickup';
  const isDeliveryOrder = checkoutForm.orderType === 'orderNowDelivery' || checkoutForm.orderType === 'preOrderDelivery';
  const isShippingOrder = checkoutForm.orderType === 'shipping';

  const isFormValid = () => {
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailValid = checkoutForm.email && emailRegex.test(checkoutForm.email);
    
    // Phone validation (10 digits, with or without formatting)
    const phoneRegex = /^[\d\s\-\(\)]{10,}$/;
    const phoneValid = checkoutForm.phone && phoneRegex.test(checkoutForm.phone.replace(/\D/g, "")) && checkoutForm.phone.replace(/\D/g, "").length >= 10;
    
    const basicFieldsValid = checkoutForm.fullName && emailValid && phoneValid;
    
    const currentPickupLocations = pickupLocations[checkoutForm.orderType] || [];
    const pickupLocationValid = (isPickupOrder && currentPickupLocations.length > 0) 
      ? checkoutForm.pickupLocation !== '' 
      : true;
    // For Order Now - Pickup, date is not required (always today)
    // For Pre-Order - Pickup, date is required
    const pickupDateValid = (checkoutForm.orderType === 'orderNowPickup') ? true : checkoutForm.pickupDate;
    const pickupValid = isPickupOrder ? (pickupLocationValid && pickupDateValid && checkoutForm.pickupTime) : true;
    
    // For Order Now - Delivery, delivery time is required
    // For Pre-Order - Delivery, both delivery date and time window are required
    const deliveryTimeValid = (checkoutForm.orderType === 'orderNowDelivery' || checkoutForm.orderType === 'preOrderDelivery') ? checkoutForm.deliveryTime : true;
    const deliveryDateValid = (checkoutForm.orderType === 'preOrderDelivery') ? checkoutForm.pickupDate : true;
    const deliveryValid = isDeliveryOrder ? (checkoutForm.city && checkoutForm.zipCode && checkoutForm.streetAddress && deliveryTimeValid && deliveryDateValid) : true;
    
    // Payment validation removed - will be handled in payment modal
    return basicFieldsValid && pickupValid && deliveryValid;
  };

  return (
    <>
      {/* Pickup Section - Only for Pickup Orders */}
      {isPickupOrder && (
        <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
          <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="h-5 w-5 text-[rgb(41,173,211)]" />
            Pickup
          </h3>
          <div className="space-y-3">
            {/* Pickup Location */}
            {(() => {
              const currentPickupLocations = pickupLocations[checkoutForm.orderType] || [];
              return currentPickupLocations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Pickup Location *</label>
                  <select
                    value={checkoutForm.pickupLocation}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, pickupLocation: e.target.value })}
                    className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                  >
                    <option value="">Select pickup location</option>
                    {currentPickupLocations.map((location, index) => (
                      <option key={index} value={index}>
                        {location.name} - {location.address}
                      </option>
                    ))}
                  </select>
                  {checkoutForm.pickupLocation !== '' && currentPickupLocations[checkoutForm.pickupLocation]?.phone && (
                    <p className="text-xs text-gray-400 mt-1.5">
                      {currentPickupLocations[checkoutForm.pickupLocation].phone}
                    </p>
                  )}
                </div>
              );
            })()}
            
            {/* Pickup Date - Only for Pre-Order Pickup */}
            {checkoutForm.orderType === 'preOrderPickup' && (() => {
              const availableDates = calculatePreOrderPickupDates();
              return (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Pickup Date *</label>
                  <select
                    value={checkoutForm.pickupDate}
                    onChange={(e) => {
                      setCheckoutForm({ 
                        ...checkoutForm, 
                        pickupDate: e.target.value,
                        pickupTime: '' // Reset pickup time when date changes
                      });
                    }}
                    className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                  >
                    <option value="">Select pickup date</option>
                    {availableDates.map((date, index) => (
                      <option key={index} value={date.value}>
                        {date.label}
                      </option>
                    ))}
                  </select>
                  {availableDates.length === 0 && (
                    <p className="text-xs text-yellow-400 mt-1.5">
                      ⚠️ Pre-order pickup is not available at this time. Please contact the store.
                    </p>
                  )}
                </div>
              );
            })()}
            
            {/* Pickup Time */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                {checkoutForm.orderType === 'orderNowPickup' ? 'Estimated Pickup Time *' : 'Pickup Time *'}
              </label>
              <select
                value={checkoutForm.pickupTime}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, pickupTime: e.target.value })}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              >
                <option value="">
                  {checkoutForm.orderType === 'orderNowPickup' 
                    ? (availablePickupTimes.length === 0 ? 'Store is closing soon - no times available' : 'Select estimated pickup time')
                    : 'Select pickup time'}
                </option>
                {(() => {
                  // Use different function based on order type
                  const times = checkoutForm.orderType === 'preOrderPickup' 
                    ? calculatePreOrderPickupTimes(checkoutForm.pickupDate)
                    : availablePickupTimes;
                  
                  return times.map((time, index) => (
                    <option key={index} value={time.value}>
                      {time.label}
                    </option>
                  ));
                })()}
              </select>
              {(() => {
                if (checkoutForm.orderType === 'preOrderPickup') {
                  // For pre-orders, check if date is selected first
                  if (!checkoutForm.pickupDate) {
                    return (
                      <p className="text-xs text-yellow-400 mt-1.5">
                        ⚠️ Please select a pickup date first.
                      </p>
                    );
                  }
                  
                  const times = calculatePreOrderPickupTimes(checkoutForm.pickupDate);
                  if (times.length === 0) {
                    return (
                      <p className="text-xs text-yellow-400 mt-1.5">
                        ⚠️ No pickup times available for this date.
                      </p>
                    );
                  }
                } else {
                  // For order now, check if times are available
                  if (availablePickupTimes.length === 0) {
                    return (
                      <p className="text-xs text-yellow-400 mt-1.5">
                        ⚠️ Store is closing soon. No pickup times available for today. Please try pre-order or come back tomorrow.
                      </p>
                    );
                  }
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delivery Time Section - Only for Order Now Delivery */}
      {checkoutForm.orderType === 'orderNowDelivery' && (
        <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
          <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="h-5 w-5 text-[rgb(41,173,211)]" />
            Delivery Time
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Estimated Delivery Time *</label>
              <select
                value={checkoutForm.deliveryTime}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, deliveryTime: e.target.value })}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              >
                <option value="">Select estimated delivery time</option>
                {availableDeliveryTimes.map((time, index) => (
                  <option key={index} value={time.value}>
                    {time.label}
                  </option>
                ))}
              </select>
              {availableDeliveryTimes.length === 0 && (
                <p className="text-xs text-yellow-400 mt-1.5">
                  ⚠️ Store is closing soon. No delivery times available for today. Please try pre-order or come back tomorrow.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pre-Order Delivery Date & Time Section - Only for Pre-Order Delivery */}
      {checkoutForm.orderType === 'preOrderDelivery' && (
        <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
          <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="h-5 w-5 text-[rgb(41,173,211)]" />
            Delivery Date & Time
          </h3>
          <div className="space-y-3">
            {/* Delivery Date */}
            {(() => {
              const availableDates = calculatePreOrderDeliveryDates();
              return (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Delivery Date *</label>
                  <select
                    value={checkoutForm.pickupDate}
                    onChange={(e) => {
                      setCheckoutForm({ 
                        ...checkoutForm, 
                        pickupDate: e.target.value,
                        deliveryTime: '' // Reset delivery time when date changes
                      });
                    }}
                    className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                  >
                    <option value="">Select delivery date</option>
                    {availableDates.map((date, index) => (
                      <option key={index} value={date.value}>
                        {date.label}
                      </option>
                    ))}
                  </select>
                  {availableDates.length === 0 && (
                    <p className="text-xs text-yellow-400 mt-1.5">
                      ⚠️ Pre-order delivery is not available at this time. Please contact the store.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Delivery Time Range */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Delivery Time Window *</label>
              <select
                value={checkoutForm.deliveryTime}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, deliveryTime: e.target.value })}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              >
                <option value="">Select delivery time window</option>
                {(() => {
                  const timeRanges = calculatePreOrderDeliveryTimeRanges(checkoutForm.pickupDate);
                  return timeRanges.map((range, index) => (
                    <option key={index} value={range.value}>
                      {range.label}
                    </option>
                  ));
                })()}
              </select>
              {(() => {
                if (!checkoutForm.pickupDate) {
                  return (
                    <p className="text-xs text-yellow-400 mt-1.5">
                      ⚠️ Please select a delivery date first.
                    </p>
                  );
                }
                
                const timeRanges = calculatePreOrderDeliveryTimeRanges(checkoutForm.pickupDate);
                if (timeRanges.length === 0) {
                  return (
                    <p className="text-xs text-yellow-400 mt-1.5">
                      ⚠️ No delivery time windows available for this date.
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delivery Address Section - Only for Delivery Orders */}
      {isDeliveryOrder && (
        <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
          <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[rgb(41,173,211)]" />
            Delivery Address
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">City *</label>
              <select
                value={checkoutForm.city}
                onChange={(e) => handleCityChange(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              >
                <option value="">Select city</option>
                {deliveryAreas.map((area, index) => (
                  <option key={index} value={area.cityName}>
                    {area.cityName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Zip Code *</label>
              <select
                value={checkoutForm.zipCode}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, zipCode: e.target.value })}
                disabled={!checkoutForm.city}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <option value="">Select zip code</option>
                {availableZipCodes.map((zip, index) => (
                  <option key={index} value={zip}>
                    {zip}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Street Address *</label>
              <textarea
                value={checkoutForm.streetAddress}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, streetAddress: e.target.value })}
                rows={2}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                placeholder="Enter your street address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Delivery Instructions</label>
              <textarea
                value={checkoutForm.deliveryInstructions}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, deliveryInstructions: e.target.value })}
                rows={2}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                placeholder="Gate code, building number, etc."
              />
            </div>
          </div>
        </div>
      )}

      {/* Shipping Address Section - Only for Shipping Orders */}
      {isShippingOrder && (
        <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
          <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[rgb(41,173,211)]" />
            Shipping Address
          </h3>
          <div className="space-y-3">
            {/* Shipping Speed Selection - Only for Flat Shipping */}
            {ecommerceSettings?.shipping?.shippingType === 'flat' && ecommerceSettings?.shipping?.flatShippingOptions?.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Select Shipping Speed *</label>
                <div className="space-y-2">
                  {ecommerceSettings.shipping.flatShippingOptions.map((option, index) => (
                    <label
                      key={index}
                      className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                        checkoutForm.selectedShippingOption === index
                          ? 'border-[rgb(41,173,211)] bg-gray-700'
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <input
                        type="radio"
                        name="shippingOption"
                        value={index}
                        checked={checkoutForm.selectedShippingOption === index}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, selectedShippingOption: parseInt(e.target.value) })}
                        className="mt-1 h-4 w-4 text-[rgb(41,173,211)] focus:ring-[rgb(41,173,211)]"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-white">{option.name}</span>
                          <span className="text-sm font-semibold text-[rgb(41,173,211)]">${option.price.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{option.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Country *</label>
              <select
                value={checkoutForm.shippingCountry || ''}
                onChange={(e) => handleShippingCountryChange(e.target.value)}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              >
                <option value="">Select country</option>
                {shippingCountries.map((country, index) => (
                  <option key={index} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">State/Region *</label>
              <select
                value={checkoutForm.shippingState || ''}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, shippingState: e.target.value })}
                disabled={!checkoutForm.shippingCountry}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <option value="">Select state/region</option>
                {availableStates.map((state, index) => (
                  <option key={index} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Street Address *</label>
              <textarea
                value={checkoutForm.shippingStreetAddress || ''}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, shippingStreetAddress: e.target.value })}
                rows={2}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                placeholder="Enter your street address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">City *</label>
              <input
                type="text"
                value={checkoutForm.shippingCity || ''}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, shippingCity: e.target.value })}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                placeholder="Enter city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Zip/Postal Code *</label>
              <input
                type="text"
                value={checkoutForm.shippingZipCode || ''}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, shippingZipCode: e.target.value })}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                placeholder="Enter zip/postal code"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Shipping Instructions</label>
              <textarea
                value={checkoutForm.shippingInstructions || ''}
                onChange={(e) => setCheckoutForm({ ...checkoutForm, shippingInstructions: e.target.value })}
                rows={2}
                className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                placeholder="Any special instructions for shipping"
              />
            </div>
          </div>
        </div>
      )}

      {/* Order at Table - Location & Table Information */}
      {checkoutForm.orderType === 'orderAtTable' && businessInfo && (
        <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
          <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[rgb(41,173,211)]" />
            Order Details
          </h3>
          <div className="space-y-3">
            {/* Location Info */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Location</label>
              <div className="bg-gray-700 text-white rounded-lg border border-gray-600 px-3 py-2.5">
                <p className="text-sm font-semibold">
                  {businessInfo.website_title || businessInfo.hospital_name || 'Restaurant'}
                </p>
                <p className="text-xs text-gray-400 mt-1">{businessInfo.address || 'Address not available'}</p>
              </div>
            </div>

            {/* Table Info */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Table</label>
              <div className="bg-gray-700 text-white rounded-lg border border-gray-600 px-3 py-2.5">
                <p className="text-sm font-medium">{checkoutForm.tableName}</p>
              </div>
            </div>

            {/* Order Time */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Order Time</label>
              <div className="bg-gray-700 text-white rounded-lg border border-gray-600 px-3 py-2.5">
                <p className="text-sm font-medium">
                  {new Date().toLocaleString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true,
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Information */}
      <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
        <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
          <User className="h-5 w-5 text-[rgb(41,173,211)]" />
          Customer Information
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Full Name *</label>
            <input
              type="text"
              value={checkoutForm.fullName}
              onChange={(e) => setCheckoutForm({ ...checkoutForm, fullName: e.target.value })}
              className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              style={{
                WebkitBoxShadow: '0 0 0 1000px rgb(55, 65, 81) inset',
                WebkitTextFillColor: 'white'
              }}
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Email *</label>
            <input
              type="email"
              value={checkoutForm.email}
              onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })}
              className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              style={{
                WebkitBoxShadow: '0 0 0 1000px rgb(55, 65, 81) inset',
                WebkitTextFillColor: 'white'
              }}
              placeholder="john@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Phone Number *</label>
            <input
              type="tel"
              value={checkoutForm.phone}
              onChange={(e) => setCheckoutForm({ ...checkoutForm, phone: e.target.value })}
              className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              style={{
                WebkitBoxShadow: '0 0 0 1000px rgb(55, 65, 81) inset',
                WebkitTextFillColor: 'white'
              }}
              placeholder="+1 (555) 123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Special Instructions</label>
            <textarea
              value={checkoutForm.specialInstructions}
              onChange={(e) => setCheckoutForm({ ...checkoutForm, specialInstructions: e.target.value })}
              rows={2}
              className="w-full px-3 py-2.5 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
              placeholder="Any special requests or dietary restrictions..."
            />
          </div>
        </div>
      </div>

      {/* Coupon Code Section */}
      {storeSettings?.coupons?.enabled && (
        <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
          <div className="flex justify-between items-center">
            {!appliedCoupon && (
              <button
                onClick={() => setShowCouponInput(!showCouponInput)}
                className="text-sm text-[rgb(41,173,211)] hover:text-[rgb(31,163,201)] flex items-center gap-1"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
                <span>{showCouponInput ? 'Hide' : 'Enter'} Coupon Code</span>
              </button>
            )}
            
            {appliedCoupon && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-900 bg-opacity-30 border border-green-500 rounded-lg">
                <svg className="h-4 w-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-green-400 font-medium">{appliedCoupon.code} Applied</span>
                <button
                  onClick={handleRemoveCoupon}
                  className="text-green-400 hover:text-green-300 ml-1"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
          
          {/* Coupon Input (Sliding Down) */}
          {showCouponInput && !appliedCoupon && (
            <div className="mt-3 p-3 bg-gray-700 rounded-lg border border-gray-600">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  className="flex-1 px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()}
                />
                <button
                  onClick={handleApplyCoupon}
                  disabled={applyingCoupon || !couponCode.trim()}
                  className="px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-lg hover:bg-[rgb(31,163,201)] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {applyingCoupon ? 'Applying...' : 'Apply'}
                </button>
              </div>
              {couponError && (
                <p className="text-xs text-red-400 mt-2">{couponError}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tip Selection - Only for Order at Table (NOT for PIN orders) */}
      {checkoutForm.orderType === 'orderAtTable' && 
       ecommerceSettings?.orderAtTable?.enableTip && 
       !(ecommerceSettings?.orderAtTable?.enablePIN && !ecommerceSettings?.orderAtTable?.enablePayments) && (
        <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
          <h3 className="text-base font-semibold text-white mb-3">Add Tip</h3>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {ecommerceSettings.orderAtTable.tipOptions.map((percentage) => (
              <button
                key={percentage}
                type="button"
                onClick={() => {
                  setSelectedTipOption(percentage);
                  setCustomTipAmount('');
                }}
                className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  selectedTipOption === percentage
                    ? 'bg-[rgb(41,173,211)] text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {percentage}%
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setSelectedTipOption('custom');
                setCustomTipAmount('');
              }}
              className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                selectedTipOption === 'custom'
                  ? 'bg-[rgb(41,173,211)] text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Custom
            </button>
          </div>
          
          {selectedTipOption === 'custom' && (
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={customTipAmount}
                onChange={(e) => setCustomTipAmount(e.target.value)}
                placeholder="Enter tip amount"
                className="w-full pl-8 pr-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
              />
            </div>
          )}
          
          {selectedTipOption && selectedTipOption !== 'custom' && (
            <p className="text-xs text-gray-400 mt-2">
              Tip amount: ${calculateTip(getCartTotal()).toFixed(2)}
            </p>
          )}
        </div>
      )}

      {/* Order Summary */}
      <div className="bg-gray-750 rounded-lg p-4 border border-gray-600">
        <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-[rgb(41,173,211)]" />
          Order Summary
        </h3>
        <div className="space-y-2">
          {cart.map(item => (
            <div key={item.cartItemId || item.id} className="text-sm">
              <div className="flex items-center justify-between text-gray-300">
                <span className="font-medium">{item.name} (x{item.quantity})</span>
                <span>${(item.price * item.quantity).toFixed(2)}</span>
              </div>
              
              {/* Display selected variant */}
              {item.variant && (
                <div className="text-xs text-blue-300 mt-0.5 ml-2">
                  • {item.variant.name}
                </div>
              )}
              
              {/* Display selected options */}
              {item.selectedOptions && item.options && (
                <div className="text-xs text-gray-400 mt-0.5 ml-2 space-y-0.5">
                  {item.options.map(option => {
                    const selectedValue = item.selectedOptions[option.option_id];
                    if (!selectedValue) return null;
                    
                    if (option.option_type === 'single') {
                      return (
                        <div key={option.option_id}>
                          • {option.option_name}
                          {option.price && ` (+$${parseFloat(option.price).toFixed(2)})`}
                        </div>
                      );
                    } else if (option.option_type === 'dropdown') {
                      const choice = option.choices?.find(c => c.choice_id === selectedValue);
                      if (!choice) return null;
                      return (
                        <div key={option.option_id}>
                          • {option.option_name}: {choice.choice_name}
                          {choice.price && ` (+$${parseFloat(choice.price).toFixed(2)})`}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              )}
            </div>
          ))}
          <div className="border-t border-gray-600 pt-2 mt-2 space-y-1">
            <div className="flex items-center justify-between text-gray-300 text-sm">
              <span>Subtotal:</span>
              <span>${getCartTotal().toFixed(2)}</span>
            </div>
            {(() => {
              const discount = calculateDiscount(getCartTotal(), checkoutForm.orderType);
              return discount > 0 && (
                <div className="flex items-center justify-between text-green-400 text-sm">
                  <span>Pre-Order Discount:</span>
                  <span>-${discount.toFixed(2)}</span>
                </div>
              );
            })()}
            {(() => {
              const couponDiscount = calculateCouponDiscount ? calculateCouponDiscount(getCartTotal()) : 0;
              return couponDiscount > 0 && (
                <div className="flex items-center justify-between text-green-400 text-sm">
                  <span>Coupon ({appliedCoupon?.code}):</span>
                  <span>-${couponDiscount.toFixed(2)}</span>
                </div>
              );
            })()}
            {/* Hide tax for PIN-based Order at Table */}
            {storeSettings?.tax?.enabled && 
             !(checkoutForm.orderType === 'orderAtTable' && 
               ecommerceSettings?.orderAtTable?.enablePIN && 
               !ecommerceSettings?.orderAtTable?.enablePayments) && (
              <div className="flex items-center justify-between text-gray-300 text-sm">
                <span>Tax ({storeSettings.tax.taxType === 'custom' ? storeSettings.tax.customTaxRate : storeSettings.tax.taxRate}%):</span>
                <span>${calculateTax(getCartTotal()).toFixed(2)}</span>
              </div>
            )}
            {/* Hide tip for PIN-based Order at Table */}
            {(() => {
              const tip = calculateTip ? calculateTip(getCartTotal()) : 0;
              const isPINOrder = checkoutForm.orderType === 'orderAtTable' && 
                                ecommerceSettings?.orderAtTable?.enablePIN && 
                                !ecommerceSettings?.orderAtTable?.enablePayments;
              return tip > 0 && !isPINOrder && (
                <div className="flex items-center justify-between text-green-400 text-sm">
                  <span>Tip:</span>
                  <span>${tip.toFixed(2)}</span>
                </div>
              );
            })()}
            {checkoutForm.orderType === 'shipping' && (
              <div className="flex items-center justify-between text-gray-300 text-sm">
                <span>Shipping Fee:</span>
                <span>
                  {(() => {
                    const fee = calculateShippingFee();
                    return fee === 0 ? 'Free' : `$${fee.toFixed(2)}`;
                  })()}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-white font-semibold text-base mt-1.5 pt-1.5 border-t border-gray-600">
              <span>Total:</span>
              <span>${(() => {
                const subtotal = getCartTotal();
                const discount = calculateDiscount(subtotal, checkoutForm.orderType);
                const couponDiscount = calculateCouponDiscount ? calculateCouponDiscount(subtotal) : 0;
                const isPINOrder = checkoutForm.orderType === 'orderAtTable' && 
                                   ecommerceSettings?.orderAtTable?.enablePIN && 
                                   !ecommerceSettings?.orderAtTable?.enablePayments;
                const tax = isPINOrder ? 0 : calculateTax(subtotal);
                const tip = isPINOrder ? 0 : (calculateTip ? calculateTip(subtotal) : 0);
                const shippingFee = calculateShippingFee();
                return (subtotal - discount - couponDiscount + tax + tip + shippingFee).toFixed(2);
              })()}</span>
            </div>
          </div>
        </div>
      </div>

    </>
  );
};

// Checkout Footer Component with Stripe Access
const CheckoutFooter = ({
  checkoutForm,
  isPlacingOrder,
  getCartTotal,
  calculateTax,
  calculateDiscount,
  calculateCouponDiscount,
  calculateShippingFee,
  calculateTip,
  appliedCoupon,
  handlePlaceOrder,
  storeSettings,
  cardComplete,
  pickupLocations,
  onPayNowClick,
  ecommerceSettings
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const isPickupOrder = checkoutForm.orderType === 'orderNowPickup' || checkoutForm.orderType === 'preOrderPickup';
  const isDeliveryOrder = checkoutForm.orderType === 'orderNowDelivery' || checkoutForm.orderType === 'preOrderDelivery';
  const isShippingOrder = checkoutForm.orderType === 'shipping';

  const isFormValid = () => {
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailValid = checkoutForm.email && emailRegex.test(checkoutForm.email);
    
    // Phone validation (10 digits, with or without formatting)
    const phoneRegex = /^[\d\s\-\(\)]{10,}$/;
    const phoneValid = checkoutForm.phone && phoneRegex.test(checkoutForm.phone.replace(/\D/g, "")) && checkoutForm.phone.replace(/\D/g, "").length >= 10;
    
    const basicFieldsValid = checkoutForm.fullName && emailValid && phoneValid;
    
    const currentPickupLocations = pickupLocations[checkoutForm.orderType] || [];
    const pickupLocationValid = (isPickupOrder && currentPickupLocations.length > 0) 
      ? checkoutForm.pickupLocation !== '' 
      : true;
    // For Order Now - Pickup, date is not required (always today)
    // For Pre-Order - Pickup, date is required
    const pickupDateValid = (checkoutForm.orderType === 'orderNowPickup') ? true : checkoutForm.pickupDate;
    const pickupValid = isPickupOrder ? (pickupLocationValid && pickupDateValid && checkoutForm.pickupTime) : true;
    
    // For Order Now - Delivery, delivery time is required
    // For Pre-Order - Delivery, both delivery date and time window are required
    const deliveryTimeValid = (checkoutForm.orderType === 'orderNowDelivery' || checkoutForm.orderType === 'preOrderDelivery') ? checkoutForm.deliveryTime : true;
    const deliveryDateValid = (checkoutForm.orderType === 'preOrderDelivery') ? checkoutForm.pickupDate : true;
    const deliveryValid = isDeliveryOrder ? (checkoutForm.city && checkoutForm.zipCode && checkoutForm.streetAddress && deliveryTimeValid && deliveryDateValid) : true;
    
    // Payment validation removed - will be handled in payment modal
    return basicFieldsValid && pickupValid && deliveryValid;
  };

  const subtotal = getCartTotal();
  const discount = calculateDiscount(subtotal, checkoutForm.orderType);
  const couponDiscount = calculateCouponDiscount ? calculateCouponDiscount(subtotal) : 0;
  const tax = calculateTax(subtotal);
  const tip = calculateTip ? calculateTip(subtotal) : 0;
  const shippingFee = calculateShippingFee();
  const total = subtotal - discount - couponDiscount + tax + tip + shippingFee;

  return (
    <div className="bg-gray-800 border-t border-gray-600 px-4 py-2 flex items-center justify-center sticky bottom-0">
      <button
        onClick={onPayNowClick}
        disabled={isPlacingOrder || !isFormValid()}
        className="px-6 py-2 bg-[rgb(41,173,211)] text-white font-semibold rounded-lg hover:bg-[rgb(31,163,201)] disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
      >
        {isPlacingOrder ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            <span>Processing...</span>
          </>
        ) : (
          <>
            {checkoutForm.orderType === 'orderAtTable' && ecommerceSettings?.orderAtTable?.enablePIN ? (
              <>
                <CheckCircle className="h-5 w-5" />
                <span>Order Now - ${total.toFixed(2)}</span>
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5" />
                <span>Pay Now - ${total.toFixed(2)}</span>
              </>
            )}
          </>
        )}
      </button>
    </div>
  );
};

const OrderOnline = () => {
  const location = useLocation();
  
  // State management with localStorage persistence
  const [menuCategories, setMenuCategories] = useState([]);
  const [activeMenuCategory, setActiveMenuCategory] = useState(() => {
    return localStorage.getItem('orderOnline_activeMenuCategory') || null;
  });
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [selectedProductCategory, setSelectedProductCategory] = useState(() => {
    return localStorage.getItem('orderOnline_selectedCategory') || 'all';
  });
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('orderOnline_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [addingToCart, setAddingToCart] = useState(null); // Track which product is being added
  const [ecommerceSettings, setEcommerceSettings] = useState(null);
  const [enabledOrderTypes, setEnabledOrderTypes] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [storeSettings, setStoreSettings] = useState(null);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [showOrderTypeModal, setShowOrderTypeModal] = useState(false);
  const [deliveryAreas, setDeliveryAreas] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [availableZipCodes, setAvailableZipCodes] = useState([]);
  const [shippingCountries, setShippingCountries] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [pickupLocations, setPickupLocations] = useState({ orderNowPickup: [], preOrderPickup: [] });
  const [storeHours, setStoreHours] = useState(null);
  const [businessTimezone, setBusinessTimezone] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  
  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  
  // PIN modal state
  const [showPINModal, setShowPINModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinValidating, setPinValidating] = useState(false);
  
  // Tip state
  const [selectedTipOption, setSelectedTipOption] = useState(null); // percentage or 'custom'
  const [customTipAmount, setCustomTipAmount] = useState('');
  
  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Coupon state
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  // Product customization modal state
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [customizingProduct, setCustomizingProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [customizeQuantity, setCustomizeQuantity] = useState(1);
  
  // Image preview modal state
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImages, setPreviewImages] = useState([]); // Array of all images for slider
  const [previewImageIndex, setPreviewImageIndex] = useState(0); // Current image index
  const [imageZoom, setImageZoom] = useState(1); // Zoom level (1 = 100%)
  const [isFullscreen, setIsFullscreen] = useState(false); // Fullscreen mode
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 }); // Pan position
  const [isDragging, setIsDragging] = useState(false); // Is currently dragging
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 }); // Drag start position

  // Bargain Modal state
  const [showBargainModal, setShowBargainModal] = useState(false);
  const [bargainProduct, setBargainProduct] = useState(null);
  const [bargainForm, setBargainForm] = useState({
    customerQuote: '',
    quantity: 1,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    notes: ''
  });
  const [bargainSubmitting, setBargainSubmitting] = useState(false);
  const [bargainError, setBargainError] = useState('');
  const [bargainSuccess, setBargainSuccess] = useState(false);

  // Checkout form state
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    orderType: '', // Will be set dynamically based on enabled order types
    orderTypeLabel: '',
    // Pickup fields
    pickupLocation: '',
    pickupDate: '',
    pickupTime: '',
    // Delivery fields
    city: '',
    zipCode: '',
    streetAddress: '',
    deliveryInstructions: '',
    deliveryTime: '',
    specialInstructions: '',
    paymentMethod: 'card', // card, cash
    // Table fields (for Order at Table)
    tableId: '',
    tableName: ''
  });

  // Check for orderAtTable parameter in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const orderType = searchParams.get('type');
    
    if (orderType === 'orderAtTable') {
      // Get table info from sessionStorage
      const tableInfo = sessionStorage.getItem('orderAtTable');
      if (tableInfo) {
        const parsedTableInfo = JSON.parse(tableInfo);
        console.log('🍽️ Order at Table detected:', parsedTableInfo);
        
        // Set the order type automatically
        setCheckoutForm(prev => ({
          ...prev,
          orderType: 'orderAtTable',
          orderTypeLabel: 'Order at Table',
          tableId: parsedTableInfo.tableId,
          tableName: parsedTableInfo.tableName
        }));
      }
    }
  }, [location]);

  // Fetch menu categories
  useEffect(() => {
    fetchMenuCategories();
    fetchPaymentSettings();
    fetchEcommerceSettings();
    fetchStoreSettings();
    fetchStoreHours();
    fetchBusinessInfo();
  }, []);

  // Fetch products when menu category changes
  useEffect(() => {
    if (activeMenuCategory) {
      fetchProducts(activeMenuCategory);
    }
  }, [activeMenuCategory]);

  // Filter products by category
  useEffect(() => {
    if (selectedProductCategory === 'all') {
      setFilteredProducts(products.filter(p => p.in_stock));
    } else {
      setFilteredProducts(products.filter(p => 
        p.in_stock && p.category === selectedProductCategory
      ));
    }
    
    // Scroll to top when filter changes (but not on initial load)
    if (products.length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedProductCategory, products]);

  // Persist cart to localStorage
  useEffect(() => {
    localStorage.setItem('orderOnline_cart', JSON.stringify(cart));
  }, [cart]);

  // Persist active menu category to localStorage
  useEffect(() => {
    if (activeMenuCategory) {
      localStorage.setItem('orderOnline_activeMenuCategory', activeMenuCategory);
    }
  }, [activeMenuCategory]);

  // Persist selected product category to localStorage
  useEffect(() => {
    localStorage.setItem('orderOnline_selectedCategory', selectedProductCategory);
  }, [selectedProductCategory]);

  const fetchMenuCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/menu-categories`);
      if (response.ok) {
        const data = await response.json();
        setMenuCategories(data);
        
        // Check if saved category exists in available categories
        const savedCategory = localStorage.getItem('orderOnline_activeMenuCategory');
        const categoryExists = savedCategory && data.some(cat => cat.id === savedCategory);
        
        if (categoryExists) {
          setActiveMenuCategory(savedCategory);
        } else if (data.length > 0) {
          setActiveMenuCategory(data[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching menu categories:', error);
    }
  };

  const fetchProducts = async (menuCategoryId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/menu-items?menu_category_id=${menuCategoryId}`);
      if (response.ok) {
        const data = await response.json();
        // Only show in-stock items
        const inStockProducts = data.filter(p => p.in_stock);
        setProducts(inStockProducts);
        
        // Extract unique product categories (using 'category' field)
        const categories = [...new Set(inStockProducts
          .map(p => p.category)
          .filter(c => c && c.trim() !== ''))];
        console.log('Product categories found:', categories);
        setProductCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/payment-settings`);
      if (response.ok) {
        const data = await response.json();
        setPaymentSettings(data);
      }
    } catch (error) {
      // Payment settings endpoint may not exist yet, that's okay
      console.log('Payment settings not available');
    }
  };

  const fetchEcommerceSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/ecommerce-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setEcommerceSettings(data);
        
        // Build list of enabled order types
        const enabled = [];
        if (data.orderNowPickup?.enabled) {
          enabled.push({ value: 'orderNowPickup', label: 'Order Now - Pickup' });
        }
        if (data.orderNowDelivery?.enabled) {
          enabled.push({ value: 'orderNowDelivery', label: 'Order Now - Delivery' });
        }
        if (data.preOrderPickup?.enabled) {
          enabled.push({ value: 'preOrderPickup', label: 'Pre Order - Pickup' });
        }
        if (data.preOrderDelivery?.enabled) {
          enabled.push({ value: 'preOrderDelivery', label: 'Pre Order - Delivery' });
        }
        if (data.shipping?.enabled) {
          enabled.push({ value: 'shipping', label: 'Shipping' });
        }
        
        setEnabledOrderTypes(enabled);
        
        // Extract delivery areas from both orderNowDelivery and preOrderDelivery
        const allDeliveryAreas = [];
        if (data.orderNowDelivery?.deliveryAreas) {
          allDeliveryAreas.push(...data.orderNowDelivery.deliveryAreas);
        }
        if (data.preOrderDelivery?.deliveryAreas) {
          allDeliveryAreas.push(...data.preOrderDelivery.deliveryAreas);
        }
        // Remove duplicates by cityName
        const uniqueAreas = allDeliveryAreas.filter((area, index, self) =>
          index === self.findIndex((a) => a.cityName === area.cityName)
        );
        setDeliveryAreas(uniqueAreas);
        
        // Extract shipping countries and states
        if (data.shipping?.countries) {
          const enabledCountries = data.shipping.countries.filter(c => c.enabled);
          setShippingCountries(enabledCountries);
        }
        
        // Extract pickup locations based on order type
        // Combine pickup locations from both order types for initial state
        const allPickupLocations = {
          orderNowPickup: data.orderNowPickup?.pickupLocations || [],
          preOrderPickup: data.preOrderPickup?.pickupLocations || []
        };
        setPickupLocations(allPickupLocations);
        
        // Don't set default order type - let user select via modal
        // if (enabled.length > 0) {
        //   setCheckoutForm(prev => ({ ...prev, orderType: enabled[0].value }));
        // }
      }
    } catch (error) {
      console.error('Error fetching ecommerce settings:', error);
    }
  };

  const fetchStoreSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/store-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data) {
        setStoreSettings(response.data);
        // Initialize Stripe if payment is enabled
        if (response.data.payments?.enabled && response.data.payments?.selectedPaymentMethod === 'stripe') {
          getStripePromise().then(() => setStripeLoaded(true));
        }
      }
    } catch (error) {
      console.error('Error fetching store settings:', error);
    }
  };

  // Cart functions
  const addToCart = (product) => {
    // Check if product has variants or options
    const hasVariantsOrOptions = product.is_variable || product.has_options;
    
    if (hasVariantsOrOptions) {
      // Show customization modal
      setCustomizingProduct(product);
      setSelectedVariant(product.is_variable ? product.variants?.[0] : null);
      setSelectedOptions({});
      setCustomizeQuantity(1);
      setShowCustomizeModal(true);
    } else {
      // Add directly to cart for simple products
      setAddingToCart(product.id);
      
      // Simulate processing with a brief delay
      setTimeout(() => {
        const existingItem = cart.find(item => item.id === product.id && !item.variant && !item.selectedOptions);
        if (existingItem) {
          setCart(cart.map(item =>
            item.id === product.id && !item.variant && !item.selectedOptions
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ));
        } else {
          setCart([...cart, { ...product, quantity: 1 }]);
        }
        setAddingToCart(null);
      }, 400); // 400ms delay for smooth feedback
    }
  };

  const addCustomizedProductToCart = () => {
    if (!customizingProduct) return;
    
    // Calculate the total price including variant price and options
    let totalPrice = customizingProduct.price;
    
    // If product has variants, use variant price
    if (customizingProduct.is_variable && selectedVariant) {
      totalPrice = parseFloat(selectedVariant.price) || 0;
    }
    
    // Add option prices
    if (customizingProduct.has_options && customizingProduct.options) {
      customizingProduct.options.forEach(option => {
        const selectedOptionValue = selectedOptions[option.option_id];
        
        if (option.option_type === 'single' && selectedOptionValue) {
          // Single option selected (checkbox)
          totalPrice += parseFloat(option.price) || 0;
        } else if (option.option_type === 'dropdown' && selectedOptionValue) {
          // Dropdown option selected
          const choice = option.choices?.find(c => c.choice_id === selectedOptionValue);
          if (choice && choice.price) {
            totalPrice += parseFloat(choice.price) || 0;
          }
        }
      });
    }
    
    // Check if an identical item already exists in the cart
    const variantMatch = selectedVariant ? selectedVariant.name : null;
    const optionsMatch = Object.keys(selectedOptions).length > 0 ? JSON.stringify(selectedOptions) : null;
    
    const existingItemIndex = cart.findIndex(item => {
      // Check if product ID matches
      if (item.id !== customizingProduct.id) return false;
      
      // Check if variant matches
      const itemVariant = item.variant ? item.variant.name : null;
      if (itemVariant !== variantMatch) return false;
      
      // Check if options match
      const itemOptions = item.selectedOptions ? JSON.stringify(item.selectedOptions) : null;
      if (itemOptions !== optionsMatch) return false;
      
      return true;
    });
    
    if (existingItemIndex !== -1) {
      // Item with same configuration exists, update quantity
      const updatedCart = [...cart];
      updatedCart[existingItemIndex] = {
        ...updatedCart[existingItemIndex],
        quantity: updatedCart[existingItemIndex].quantity + customizeQuantity
      };
      setCart(updatedCart);
    } else {
      // Create new cart item with customizations
      const cartItem = {
        ...customizingProduct,
        quantity: customizeQuantity,
        price: totalPrice,
        originalPrice: customizingProduct.price,
        variant: selectedVariant ? {
          name: selectedVariant.name,
          price: selectedVariant.price
        } : null,
        selectedOptions: Object.keys(selectedOptions).length > 0 ? { ...selectedOptions } : null,
        // Generate a unique key for this configuration
        cartItemId: `${customizingProduct.id}-${Date.now()}-${Math.random()}`
      };
      
      // Add to cart
      setCart([...cart, cartItem]);
    }
    
    // Close modal
    setShowCustomizeModal(false);
    setCustomizingProduct(null);
    setSelectedVariant(null);
    setSelectedOptions({});
    setCustomizeQuantity(1);
  };

  const updateQuantity = (productId, change) => {
    setCart(cart.map(item =>
      item.id === productId
        ? { ...item, quantity: Math.max(1, item.quantity + change) }
        : item
    ).filter(item => item.quantity > 0));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  // Bargain Functions
  const openBargainModal = (product) => {
    setBargainProduct(product);
    setBargainForm({
      customerQuote: '',
      quantity: 1,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      notes: ''
    });
    setBargainError('');
    setBargainSuccess(false);
    setShowBargainModal(true);
  };

  const closeBargainModal = () => {
    setShowBargainModal(false);
    setBargainProduct(null);
    setBargainError('');
    setBargainSuccess(false);
  };

  const submitBargainRequest = async () => {
    // Validate form
    if (!bargainForm.customerQuote || parseFloat(bargainForm.customerQuote) <= 0) {
      setBargainError('Please enter a valid price offer');
      return;
    }
    
    if (!bargainForm.firstName || !bargainForm.lastName) {
      setBargainError('Please enter your full name');
      return;
    }
    
    if (!bargainForm.email) {
      setBargainError('Please enter your email address');
      return;
    }
    
    if (!bargainForm.phone) {
      setBargainError('Please enter your phone number');
      return;
    }
    
    setBargainSubmitting(true);
    setBargainError('');
    
    // Build address string from components
    const addressParts = [
      bargainForm.streetAddress,
      bargainForm.city,
      bargainForm.state,
      bargainForm.zipCode
    ].filter(part => part && part.trim());
    const fullAddress = addressParts.join(', ') || null;
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/bargain-requests`, {
        product_id: bargainProduct.id,
        customer_quote: parseFloat(bargainForm.customerQuote),
        quantity: parseInt(bargainForm.quantity) || 1,
        first_name: bargainForm.firstName,
        last_name: bargainForm.lastName,
        email: bargainForm.email,
        phone: bargainForm.phone,
        address: fullAddress,
        notes: bargainForm.notes || null
      });
      
      if (response.data.success) {
        setBargainSuccess(true);
      } else {
        setBargainError(response.data.message || 'Failed to submit bargain request');
      }
    } catch (error) {
      console.error('Error submitting bargain request:', error);
      setBargainError(error.response?.data?.detail || 'Failed to submit bargain request. Please try again.');
    } finally {
      setBargainSubmitting(false);
    }
  };

  const calculateTax = (subtotal) => {
    // Check if tax is enabled in store settings
    if (!storeSettings?.tax?.enabled) {
      return 0;
    }
    
    // Get tax rate from store settings
    let taxRate = 0;
    if (storeSettings.tax.taxType === 'custom') {
      taxRate = storeSettings.tax.customTaxRate || 0;
    } else {
      taxRate = storeSettings.tax.taxRate || 0;
    }
    
    // Convert percentage to decimal (e.g., 8.25 -> 0.0825)
    return subtotal * (taxRate / 100);
  };

  const calculateDiscount = (subtotal, orderType) => {
    // Check if this is a pre-order
    if (!orderType || (!orderType.includes('preOrder'))) {
      return 0;
    }

    // Get discount percentage based on order type
    let discountPercent = 0;
    if (orderType === 'preOrderPickup' && ecommerceSettings?.preOrderPickup?.discountPercentage) {
      discountPercent = ecommerceSettings.preOrderPickup.discountPercentage;
    } else if (orderType === 'preOrderDelivery' && ecommerceSettings?.preOrderDelivery?.discountPercentage) {
      discountPercent = ecommerceSettings.preOrderDelivery.discountPercentage;
    }

    // Calculate discount amount
    return subtotal * (discountPercent / 100);
  };

  const calculateTip = (subtotal) => {
    // Only calculate tip for Order at Table
    if (checkoutForm.orderType !== 'orderAtTable') {
      return 0;
    }

    // Check if tip is enabled
    if (!ecommerceSettings?.orderAtTable?.enableTip) {
      return 0;
    }

    // Custom tip amount
    if (selectedTipOption === 'custom') {
      return parseFloat(customTipAmount) || 0;
    }

    // Percentage tip
    if (selectedTipOption !== null && selectedTipOption !== undefined) {
      return subtotal * (selectedTipOption / 100);
    }

    return 0;
  };

  const calculateShippingFee = () => {
    // Only calculate shipping fee if order type is shipping
    if (checkoutForm.orderType !== 'shipping') {
      return 0;
    }

    const shippingSettings = ecommerceSettings?.shipping;
    if (!shippingSettings || !shippingSettings.enabled) {
      return 0;
    }

    // Free Shipping
    if (shippingSettings.shippingType === 'free') {
      return 0;
    }

    // Flat Fee Shipping - Use selected shipping option
    if (shippingSettings.shippingType === 'flat') {
      const selectedIndex = checkoutForm.selectedShippingOption;
      const flatOptions = shippingSettings.flatShippingOptions || [];
      
      if (selectedIndex !== undefined && selectedIndex !== null && flatOptions[selectedIndex]) {
        return parseFloat(flatOptions[selectedIndex].price) || 0;
      }
      
      // Default to first option if none selected
      if (flatOptions.length > 0) {
        return parseFloat(flatOptions[0].price) || 0;
      }
      
      return 0;
    }

    // Category-Based Shipping
    if (shippingSettings.shippingType === 'category') {
      let totalShippingFee = 0;
      const categoryShippingRates = shippingSettings.categoryShipping || [];
      
      // Check each item in cart for category-specific shipping
      cart.forEach(item => {
        // Find if this item's product category has a custom shipping rate
        const categoryRate = categoryShippingRates.find(
          rate => rate.productCategory === item.category
        );
        
        if (categoryRate) {
          // Use category-specific rate (per item or per order - using per order for now)
          totalShippingFee = Math.max(totalShippingFee, parseFloat(categoryRate.fee) || 0);
        } else {
          // Use default shipping for uncategorized products
          if (shippingSettings.categoryDefaultType === 'flat') {
            totalShippingFee = Math.max(totalShippingFee, parseFloat(shippingSettings.categoryDefaultFee) || 0);
          }
          // If default is 'free', it contributes 0 to shipping
        }
      });
      
      return totalShippingFee;
    }

    return 0;
  };

  const fetchStoreHours = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/hospital-hours`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data) {
        setStoreHours(response.data);
      }
    } catch (error) {
      console.error('Error fetching store hours:', error);
    }
  };

  const fetchBusinessInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/business-info`);
      if (response.data) {
        setBusinessInfo(response.data);
        if (response.data.timezone) {
          setBusinessTimezone(response.data.timezone);
        }
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const getStoreStatus = (orderType) => {
    if (!storeHours || !ecommerceSettings) {
      return { isAvailable: true, isClosed: false, isClosingSoon: false, warningClosingSoon: false };
    }

    // Get current date/time in business timezone if available
    let currentDate = new Date();
    if (businessTimezone) {
      try {
        // Convert to business timezone using toLocaleString
        const tzDateString = currentDate.toLocaleString('en-US', { timeZone: businessTimezone });
        currentDate = new Date(tzDateString);
      } catch (error) {
        console.error('Error converting to business timezone:', error);
        // Fall back to browser's local time if timezone conversion fails
      }
    }

    // Get current day name in full lowercase format (e.g., 'monday', 'tuesday')
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[currentDate.getDay()];
    const daySchedule = storeHours[currentDay];

    // Store is closed today (using same check as Home.jsx)
    if (!daySchedule?.is_open || !daySchedule.open_time || !daySchedule.close_time) {
      return { isAvailable: false, isClosed: true, isClosingSoon: false, warningClosingSoon: false };
    }

    // Get current time in minutes since midnight (using timezone-aware date)
    const currentTime = currentDate.getHours() * 60 + currentDate.getMinutes();

    // Parse open and close times in minutes since midnight
    const [openHour, openMin] = daySchedule.open_time.split(':').map(Number);
    const [closeHour, closeMin] = daySchedule.close_time.split(':').map(Number);
    
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;

    // Check if currently open (same logic as Home.jsx)
    const isOpen = currentTime >= openTime && currentTime < closeTime;

    // Get prep time based on order type
    let prepTimeMinutes = 30;
    if (orderType === 'orderNowPickup' && ecommerceSettings.orderNowPickup) {
      prepTimeMinutes = ecommerceSettings.orderNowPickup.prepTime || 30;
    } else if (orderType === 'orderNowDelivery' && ecommerceSettings.orderNowDelivery) {
      prepTimeMinutes = (ecommerceSettings.orderNowDelivery.prepTime || 30) + (ecommerceSettings.orderNowDelivery.deliveryTime || 30);
    }

    // If not open yet, check if within 2 hours of opening
    if (!isOpen && currentTime < openTime) {
      const minutesUntilOpen = openTime - currentTime;
      
      // Allow orders starting 2 hours (120 minutes) before opening
      if (minutesUntilOpen <= 120) {
        // Calculate earliest fulfillment time (opening time + prep time)
        const earliestFulfillmentTime = openTime + prepTimeMinutes;
        
        // Check if we can fulfill before closing
        if (earliestFulfillmentTime < closeTime) {
          return { isAvailable: true, isClosed: false, isClosingSoon: false, warningClosingSoon: false, openingSoon: true };
        }
      }
      
      // Too early to order or can't fulfill before closing
      return { isAvailable: false, isClosed: true, isClosingSoon: false, warningClosingSoon: false };
    }

    // Already closed for the day
    if (!isOpen && currentTime >= closeTime) {
      return { isAvailable: false, isClosed: true, isClosingSoon: false, warningClosingSoon: false };
    }

    // Calculate earliest completion time in minutes (for currently open store)
    const earliestCompletionTime = currentTime + prepTimeMinutes;
    
    // Calculate time until closing in minutes
    const minutesUntilClosing = closeTime - currentTime;

    // If earliest completion is after closing time, DISABLE (can't fulfill order)
    if (earliestCompletionTime >= closeTime) {
      return { isAvailable: false, isClosed: false, isClosingSoon: true, warningClosingSoon: false };
    }

    // If closing within 1 hour but can still fulfill, show WARNING but keep ENABLED
    if (minutesUntilClosing <= 60) {
      return { isAvailable: true, isClosed: false, isClosingSoon: false, warningClosingSoon: true };
    }

    return { isAvailable: true, isClosed: false, isClosingSoon: false, warningClosingSoon: false };
  };

  const isStoreClosingSoon = (orderType) => {
    const status = getStoreStatus(orderType);
    return !status.isAvailable;
  };

  // Calculate available pre-order delivery dates (same logic as pickup)
  const calculatePreOrderDeliveryDates = () => {
    if (!ecommerceSettings?.preOrderDelivery) {
      return [];
    }

    const preOrderConfig = ecommerceSettings.preOrderDelivery;
    const deliveryOption = preOrderConfig.deliveryOption;
    
    // Get current date/time in business timezone
    let currentDate = new Date();
    if (businessTimezone) {
      try {
        const tzDateString = currentDate.toLocaleString('en-US', { timeZone: businessTimezone });
        currentDate = new Date(tzDateString);
      } catch (error) {
        console.error('Error converting to business timezone:', error);
      }
    }

    const dates = [];
    let startDate = new Date(currentDate);

    // Determine the earliest available delivery date based on cutoff type
    if (deliveryOption === 'oneDayBefore') {
      // One day before: tomorrow is earliest
      startDate.setDate(startDate.getDate() + 1);
    } else if (deliveryOption === 'sameDay') {
      // Same day: check if cutoff time has passed
      const cutoffTime = preOrderConfig.sameDay?.cutoffTime || '06:00';
      const [cutoffHour, cutoffMin] = cutoffTime.split(':').map(Number);
      const cutoffMinutes = cutoffHour * 60 + cutoffMin;
      const currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes();
      
      if (currentMinutes >= cutoffMinutes) {
        // Cutoff passed, start from tomorrow
        startDate.setDate(startDate.getDate() + 1);
      }
      // Otherwise, same day is available
    } else if (deliveryOption === 'dualCutoff') {
      // Dual cutoff: check both morning and evening cutoffs
      const dualConfig = preOrderConfig.dualCutoff;
      const currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes();
      
      const morningCutoff = dualConfig.morningCutoffTime || '06:00';
      const [mCutoffHour, mCutoffMin] = morningCutoff.split(':').map(Number);
      const morningCutoffMinutes = mCutoffHour * 60 + mCutoffMin;
      
      const eveningCutoff = dualConfig.eveningCutoffTime || '12:00';
      const [eCutoffHour, eCutoffMin] = eveningCutoff.split(':').map(Number);
      const eveningCutoffMinutes = eCutoffHour * 60 + eCutoffMin;
      
      // If both cutoffs passed, start from tomorrow
      if (currentMinutes >= eveningCutoffMinutes) {
        startDate.setDate(startDate.getDate() + 1);
      }
      // Otherwise, same day is available (either morning or evening slot)
    }

    // Generate next 5 days from start date
    for (let i = 0; i < 5; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const dayName = dayNames[date.getDay()];
      const monthName = monthNames[date.getMonth()];
      const day = date.getDate();
      
      const dateValue = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      const dateLabel = `${dayName} (${monthName} ${day})`;
      
      dates.push({ value: dateValue, label: dateLabel });
    }

    return dates;
  };

  // Calculate available pre-order delivery time ranges based on selected date
  const calculatePreOrderDeliveryTimeRanges = (selectedDate) => {
    if (!ecommerceSettings?.preOrderDelivery || !selectedDate) {
      return [];
    }

    const preOrderConfig = ecommerceSettings.preOrderDelivery;
    const deliveryOption = preOrderConfig.deliveryOption;
    
    // Get current date/time in business timezone
    let currentDate = new Date();
    let currentDateInBusinessTZ = currentDate;
    
    if (businessTimezone) {
      try {
        const tzDateString = currentDate.toLocaleString('en-US', { timeZone: businessTimezone });
        currentDateInBusinessTZ = new Date(tzDateString);
      } catch (error) {
        console.error('Error converting to business timezone:', error);
      }
    }

    const currentMinutes = currentDateInBusinessTZ.getHours() * 60 + currentDateInBusinessTZ.getMinutes();
    
    // Format current date in business timezone as YYYY-MM-DD
    const currentYear = currentDateInBusinessTZ.getFullYear();
    const currentMonth = String(currentDateInBusinessTZ.getMonth() + 1).padStart(2, '0');
    const currentDay = String(currentDateInBusinessTZ.getDate()).padStart(2, '0');
    const currentDateString = `${currentYear}-${currentMonth}-${currentDay}`;
    
    const isToday = selectedDate === currentDateString;

    const ranges = [];

    // Helper function to format time range
    const formatTimeRange = (startTime, endTime) => {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      
      const formatTime = (hour, min) => {
        const displayHours = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        return `${displayHours}:${min.toString().padStart(2, '0')} ${ampm}`;
      };
      
      return `${formatTime(startHour, startMin)} - ${formatTime(endHour, endMin)}`;
    };

    if (deliveryOption === 'oneDayBefore') {
      const config = preOrderConfig.oneDayBefore;
      const startTime = config.deliveryStartTime || '12:00';
      const endTime = config.deliveryEndTime || '16:00';
      const label = formatTimeRange(startTime, endTime);
      ranges.push({ value: `${startTime}-${endTime}`, label });
    } else if (deliveryOption === 'sameDay') {
      const config = preOrderConfig.sameDay;
      const startTime = config.deliveryStartTime || '06:00';
      const endTime = config.deliveryEndTime || '18:00';
      const label = formatTimeRange(startTime, endTime);
      ranges.push({ value: `${startTime}-${endTime}`, label });
    } else if (deliveryOption === 'dualCutoff') {
      const dualConfig = preOrderConfig.dualCutoff;
      
      if (isToday) {
        // Check which slots are available based on cutoffs
        const morningCutoffTime = dualConfig.morningCutoffTime || '06:00';
        const [mCutoffHour, mCutoffMin] = morningCutoffTime.split(':').map(Number);
        const morningCutoffMinutes = mCutoffHour * 60 + mCutoffMin;
        
        const eveningCutoffTime = dualConfig.eveningCutoffTime || '12:00';
        const [eCutoffHour, eCutoffMin] = eveningCutoffTime.split(':').map(Number);
        const eveningCutoffMinutes = eCutoffHour * 60 + eCutoffMin;
        
        const showMorning = currentMinutes < morningCutoffMinutes && dualConfig.morningEnabled;
        const showEvening = currentMinutes < eveningCutoffMinutes && dualConfig.eveningEnabled;
        
        if (showMorning) {
          const startTime = dualConfig.morningDeliveryStartTime || '06:00';
          const endTime = dualConfig.morningDeliveryEndTime || '12:00';
          const label = formatTimeRange(startTime, endTime);
          ranges.push({ value: `${startTime}-${endTime}`, label });
        }
        
        if (showEvening) {
          const startTime = dualConfig.eveningDeliveryStartTime || '17:00';
          const endTime = dualConfig.eveningDeliveryEndTime || '21:00';
          const label = formatTimeRange(startTime, endTime);
          ranges.push({ value: `${startTime}-${endTime}`, label });
        }
      } else {
        // Future dates: show all enabled ranges
        if (dualConfig.morningEnabled) {
          const startTime = dualConfig.morningDeliveryStartTime || '06:00';
          const endTime = dualConfig.morningDeliveryEndTime || '12:00';
          const label = formatTimeRange(startTime, endTime);
          ranges.push({ value: `${startTime}-${endTime}`, label });
        }
        
        if (dualConfig.eveningEnabled) {
          const startTime = dualConfig.eveningDeliveryStartTime || '17:00';
          const endTime = dualConfig.eveningDeliveryEndTime || '21:00';
          const label = formatTimeRange(startTime, endTime);
          ranges.push({ value: `${startTime}-${endTime}`, label });
        }
      }
    }

    return ranges;
  };

  // Calculate available pre-order pickup dates
  const calculatePreOrderPickupDates = () => {
    if (!ecommerceSettings?.preOrderPickup) {
      return [];
    }

    const preOrderConfig = ecommerceSettings.preOrderPickup;
    const pickupOption = preOrderConfig.pickupOption;
    
    // Get current date/time in business timezone
    let currentDate = new Date();
    if (businessTimezone) {
      try {
        const tzDateString = currentDate.toLocaleString('en-US', { timeZone: businessTimezone });
        currentDate = new Date(tzDateString);
      } catch (error) {
        console.error('Error converting to business timezone:', error);
      }
    }

    const dates = [];
    let startDate = new Date(currentDate);

    // Determine the earliest available pickup date based on cutoff type
    if (pickupOption === 'oneDayBefore') {
      // One day before: tomorrow is earliest
      startDate.setDate(startDate.getDate() + 1);
    } else if (pickupOption === 'sameDay') {
      // Same day: check if cutoff time has passed
      const cutoffTime = preOrderConfig.sameDay?.cutoffTime || '06:00';
      const [cutoffHour, cutoffMin] = cutoffTime.split(':').map(Number);
      const cutoffMinutes = cutoffHour * 60 + cutoffMin;
      const currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes();
      
      if (currentMinutes >= cutoffMinutes) {
        // Cutoff passed, start from tomorrow
        startDate.setDate(startDate.getDate() + 1);
      }
      // Otherwise, same day is available
    } else if (pickupOption === 'dualCutoff') {
      // Dual cutoff: check both morning and evening cutoffs
      const dualConfig = preOrderConfig.dualCutoff;
      const currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes();
      
      const morningCutoff = dualConfig.morningCutoffTime || '06:00';
      const [mCutoffHour, mCutoffMin] = morningCutoff.split(':').map(Number);
      const morningCutoffMinutes = mCutoffHour * 60 + mCutoffMin;
      
      const eveningCutoff = dualConfig.eveningCutoffTime || '12:00';
      const [eCutoffHour, eCutoffMin] = eveningCutoff.split(':').map(Number);
      const eveningCutoffMinutes = eCutoffHour * 60 + eCutoffMin;
      
      // If both cutoffs passed, start from tomorrow
      if (currentMinutes >= eveningCutoffMinutes) {
        startDate.setDate(startDate.getDate() + 1);
      }
      // Otherwise, same day is available (either morning or evening slot)
    }

    // Generate next 5 days from start date
    for (let i = 0; i < 5; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const dayName = dayNames[date.getDay()];
      const monthName = monthNames[date.getMonth()];
      const day = date.getDate();
      
      const dateValue = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      const dateLabel = `${dayName} (${monthName} ${day})`;
      
      dates.push({ value: dateValue, label: dateLabel });
    }

    return dates;
  };

  // Calculate available pre-order pickup times based on selected date
  const calculatePreOrderPickupTimes = (selectedDate) => {
    if (!ecommerceSettings?.preOrderPickup || !selectedDate) {
      return [];
    }

    const preOrderConfig = ecommerceSettings.preOrderPickup;
    const pickupOption = preOrderConfig.pickupOption;
    
    // Get current date/time in business timezone
    let currentDate = new Date();
    let currentDateInBusinessTZ = currentDate;
    
    if (businessTimezone) {
      try {
        // Get current date/time in business timezone
        const tzDateString = currentDate.toLocaleString('en-US', { timeZone: businessTimezone });
        currentDateInBusinessTZ = new Date(tzDateString);
      } catch (error) {
        console.error('Error converting to business timezone:', error);
      }
    }

    const currentMinutes = currentDateInBusinessTZ.getHours() * 60 + currentDateInBusinessTZ.getMinutes();
    
    // Format current date in business timezone as YYYY-MM-DD for comparison
    const currentYear = currentDateInBusinessTZ.getFullYear();
    const currentMonth = String(currentDateInBusinessTZ.getMonth() + 1).padStart(2, '0');
    const currentDay = String(currentDateInBusinessTZ.getDate()).padStart(2, '0');
    const currentDateString = `${currentYear}-${currentMonth}-${currentDay}`;
    
    // Compare selectedDate (YYYY-MM-DD) with current date (YYYY-MM-DD) in business timezone
    const isToday = selectedDate === currentDateString;

    let pickupStartTime, pickupEndTime;
    const times = [];

    if (pickupOption === 'oneDayBefore') {
      pickupStartTime = preOrderConfig.oneDayBefore?.pickupStartTime || '06:00';
      pickupEndTime = preOrderConfig.oneDayBefore?.pickupEndTime || '18:00';
    } else if (pickupOption === 'sameDay') {
      pickupStartTime = preOrderConfig.sameDay?.pickupStartTime || '06:00';
      pickupEndTime = preOrderConfig.sameDay?.pickupEndTime || '18:00';
    } else if (pickupOption === 'dualCutoff') {
      const dualConfig = preOrderConfig.dualCutoff;
      
      // If today, check which slots are available based on cutoffs
      if (isToday) {
        const morningCutoffTime = dualConfig.morningCutoffTime || '06:00';
        const [mCutoffHour, mCutoffMin] = morningCutoffTime.split(':').map(Number);
        const morningCutoffMinutes = mCutoffHour * 60 + mCutoffMin;
        
        const eveningCutoffTime = dualConfig.eveningCutoffTime || '12:00';
        const [eCutoffHour, eCutoffMin] = eveningCutoffTime.split(':').map(Number);
        const eveningCutoffMinutes = eCutoffHour * 60 + eCutoffMin;
        
        // Determine which slots to show based on cutoff times
        const showMorning = currentMinutes < morningCutoffMinutes && dualConfig.morningEnabled;
        const showEvening = currentMinutes < eveningCutoffMinutes && dualConfig.eveningEnabled;
        
        if (showMorning && showEvening) {
          // Both slots available - show both morning and evening times
          // Will be handled by the code after this block
          pickupStartTime = null; // Flag to generate both slots
        } else if (showEvening) {
          // Only evening slot available
          pickupStartTime = dualConfig.eveningPickupStartTime || '17:00';
          pickupEndTime = dualConfig.eveningPickupEndTime || '21:00';
        } else if (showMorning) {
          // Only morning slot available (shouldn't happen often)
          pickupStartTime = dualConfig.morningPickupStartTime || '06:00';
          pickupEndTime = dualConfig.morningPickupEndTime || '12:00';
        }
      } else {
        // For future dates, always show both morning and evening slots
        pickupStartTime = null; // Flag to generate both slots
      }
      
      // Generate both morning and evening slots if pickupStartTime is null
      if (pickupStartTime === null) {
        // Add morning times
        if (dualConfig.morningEnabled) {
          const morningStart = dualConfig.morningPickupStartTime || '06:00';
          const morningEnd = dualConfig.morningPickupEndTime || '12:00';
          
          const [mStartHour, mStartMin] = morningStart.split(':').map(Number);
          const [mEndHour, mEndMin] = morningEnd.split(':').map(Number);
          const mStartMinutes = mStartHour * 60 + mStartMin;
          const mEndMinutes = mEndHour * 60 + mEndMin;
          
          let slotTime = Math.ceil(mStartMinutes / 15) * 15;
          while (slotTime <= mEndMinutes) {
            const hours = Math.floor(slotTime / 60);
            const mins = slotTime % 60;
            const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
            const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayTime = `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm}`;
            
            times.push({ value: timeStr, label: displayTime });
            slotTime += 15;
          }
        }
        
        // Add evening times
        if (dualConfig.eveningEnabled) {
          const eveningStart = dualConfig.eveningPickupStartTime || '17:00';
          const eveningEnd = dualConfig.eveningPickupEndTime || '21:00';
          
          const [eStartHour, eStartMin] = eveningStart.split(':').map(Number);
          const [eEndHour, eEndMin] = eveningEnd.split(':').map(Number);
          const eStartMinutes = eStartHour * 60 + eStartMin;
          const eEndMinutes = eEndHour * 60 + eEndMin;
          
          let slotTime = Math.ceil(eStartMinutes / 15) * 15;
          while (slotTime <= eEndMinutes) {
            const hours = Math.floor(slotTime / 60);
            const mins = slotTime % 60;
            const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
            const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
            const ampm = hours >= 12 ? 'PM' : 'AM';
            const displayTime = `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm}`;
            
            times.push({ value: timeStr, label: displayTime });
            slotTime += 15;
          }
        }
        
        // Return early since we've already generated times
        return times;
      }
    }

    // Generate time slots for the determined range (if not dual cutoff or if morning slot)
    if (pickupStartTime && pickupEndTime) {
      const [startHour, startMin] = pickupStartTime.split(':').map(Number);
      const [endHour, endMin] = pickupEndTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      let slotTime = Math.ceil(startMinutes / 15) * 15;
      while (slotTime <= endMinutes) {
        const hours = Math.floor(slotTime / 60);
        const mins = slotTime % 60;
        const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
        const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayTime = `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm}`;
        
        times.push({ value: timeStr, label: displayTime });
        slotTime += 15;
      }
    }

    return times;
  };

  const calculateAvailablePickupTimes = () => {
    if (!storeHours || !ecommerceSettings?.orderNowPickup) {
      return [];
    }

    // Get current date/time in business timezone if available
    let currentDate = new Date();
    if (businessTimezone) {
      try {
        const tzDateString = currentDate.toLocaleString('en-US', { timeZone: businessTimezone });
        currentDate = new Date(tzDateString);
      } catch (error) {
        console.error('Error converting to business timezone:', error);
      }
    }

    // Get current day name in full lowercase format (same as Home.jsx)
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[currentDate.getDay()];
    const daySchedule = storeHours[currentDay];

    // Check if store is open today (same logic as Home.jsx)
    if (!daySchedule?.is_open || !daySchedule.open_time || !daySchedule.close_time) {
      return [];
    }

    const currentTime = currentDate.getHours() * 60 + currentDate.getMinutes();
    
    const prepTimeMinutes = ecommerceSettings.orderNowPickup.prepTime || 30;
    
    // Parse store hours using same field names as Home.jsx
    const [openHour, openMin] = daySchedule.open_time.split(':').map(Number);
    const [closeHour, closeMin] = daySchedule.close_time.split(':').map(Number);
    
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    
    // Check if currently open (same as Home.jsx logic)
    const isCurrentlyOpen = currentTime >= openTime && currentTime < closeTime;
    
    let earliestPickupTime;
    
    // If not open yet, check if within 2 hours of opening
    if (!isCurrentlyOpen && currentTime < openTime) {
      const minutesUntilOpen = openTime - currentTime;
      
      // Allow orders starting 2 hours before opening
      if (minutesUntilOpen <= 120) {
        // Earliest pickup time is opening time + prep time
        earliestPickupTime = openTime + prepTimeMinutes;
      } else {
        return []; // Too early to order
      }
    } else if (!isCurrentlyOpen) {
      // Store is closed for the day
      return [];
    } else {
      // Store is currently open
      // Calculate earliest pickup time in minutes
      earliestPickupTime = currentTime + prepTimeMinutes;
    }
    
    // If earliest pickup is after closing time, no times available today
    if (earliestPickupTime >= closeTime) {
      return [];
    }
    
    // Generate time slots every 15 minutes from earliest pickup to closing
    const times = [];
    
    // Round up to next 15-minute interval
    let slotTime = Math.ceil(earliestPickupTime / 15) * 15;
    
    while (slotTime < closeTime) {
      const hours = Math.floor(slotTime / 60);
      const mins = slotTime % 60;
      const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayTime = `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm}`;
      
      times.push({ value: timeStr, label: displayTime });
      
      // Add 15 minutes
      slotTime += 15;
    }
    
    return times;
  };

  const calculateAvailableDeliveryTimes = () => {
    if (!storeHours || !ecommerceSettings?.orderNowDelivery) {
      return [];
    }

    // Get current date/time in business timezone if available
    let currentDate = new Date();
    if (businessTimezone) {
      try {
        const tzDateString = currentDate.toLocaleString('en-US', { timeZone: businessTimezone });
        currentDate = new Date(tzDateString);
      } catch (error) {
        console.error('Error converting to business timezone:', error);
      }
    }

    // Get current day name in full lowercase format
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[currentDate.getDay()];
    const daySchedule = storeHours[currentDay];

    // Check if store is open today
    if (!daySchedule?.is_open || !daySchedule.open_time || !daySchedule.close_time) {
      return [];
    }

    const currentTime = currentDate.getHours() * 60 + currentDate.getMinutes();
    
    const prepTimeMinutes = ecommerceSettings.orderNowDelivery.prepTime || 30;
    const deliveryTimeMinutes = ecommerceSettings.orderNowDelivery.deliveryTime || 30;
    const totalTimeMinutes = prepTimeMinutes + deliveryTimeMinutes;
    
    // Parse store hours
    const [openHour, openMin] = daySchedule.open_time.split(':').map(Number);
    const [closeHour, closeMin] = daySchedule.close_time.split(':').map(Number);
    
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    
    // Check if currently open
    const isCurrentlyOpen = currentTime >= openTime && currentTime < closeTime;
    
    let earliestDeliveryTime;
    
    // If not open yet, check if within 2 hours of opening
    if (!isCurrentlyOpen && currentTime < openTime) {
      const minutesUntilOpen = openTime - currentTime;
      
      // Allow orders starting 2 hours before opening
      if (minutesUntilOpen <= 120) {
        // Earliest delivery time is opening time + prep time + delivery time
        earliestDeliveryTime = openTime + totalTimeMinutes;
      } else {
        return []; // Too early to order
      }
    } else if (!isCurrentlyOpen) {
      // Store is closed for the day
      return [];
    } else {
      // Store is currently open
      // Calculate earliest delivery time in minutes
      earliestDeliveryTime = currentTime + totalTimeMinutes;
    }
    
    // If earliest delivery is after closing time, no times available today
    if (earliestDeliveryTime >= closeTime) {
      return [];
    }
    
    // Generate time slots every 15 minutes from earliest delivery to closing
    const times = [];
    
    // Round up to next 15-minute interval
    let slotTime = Math.ceil(earliestDeliveryTime / 15) * 15;
    
    while (slotTime < closeTime) {
      const hours = Math.floor(slotTime / 60);
      const mins = slotTime % 60;
      const timeStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      const displayHours = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayTime = `${displayHours}:${mins.toString().padStart(2, '0')} ${ampm}`;
      
      times.push({ value: timeStr, label: displayTime });
      
      // Add 15 minutes
      slotTime += 15;
    }
    
    return times;
  };

  // Helper function to extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Helper function to check if URL is a YouTube video
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  // Helper function to check if URL is a video file
  const isVideoFile = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
    return videoExtensions.some(ext => url.toLowerCase().includes(ext));
  };

  // Open image/video preview with slider support
  const openImagePreview = (product, startIndex = 0) => {
    const allMedia = [];
    
    // Add main image first if it exists
    if (product.image_url) {
      allMedia.push({ url: product.image_url, type: 'image' });
    }
    
    // Add all media gallery items (images AND videos)
    if (product.media_gallery && product.media_gallery.length > 0) {
      product.media_gallery.forEach(m => allMedia.push({ url: m.url, type: m.type || 'image' }));
    }
    
    // If no media, don't open preview
    if (allMedia.length === 0) return;
    
    setPreviewImages(allMedia);
    setPreviewImageIndex(startIndex);
    setPreviewImageUrl(allMedia[startIndex]?.url || product.image_url);
    setShowImagePreview(true);
  };

  // Navigate to previous media in preview
  const prevPreviewImage = (e) => {
    e.stopPropagation();
    if (previewImages.length > 1) {
      const newIndex = previewImageIndex === 0 ? previewImages.length - 1 : previewImageIndex - 1;
      setPreviewImageIndex(newIndex);
      setPreviewImageUrl(previewImages[newIndex].url);
    }
  };

  // Navigate to next media in preview
  const nextPreviewImage = (e) => {
    e.stopPropagation();
    if (previewImages.length > 1) {
      const newIndex = previewImageIndex === previewImages.length - 1 ? 0 : previewImageIndex + 1;
      setPreviewImageIndex(newIndex);
      setPreviewImageUrl(previewImages[newIndex].url);
    }
  };

  // Zoom in function
  const zoomIn = (e) => {
    e.stopPropagation();
    setImageZoom(prev => {
      const newZoom = Math.min(prev + 0.05, 3);
      return Math.round(newZoom * 100) / 100; // Round to 2 decimal places
    });
  };

  // Zoom out function
  const zoomOut = (e) => {
    e.stopPropagation();
    setImageZoom(prev => {
      const newZoom = Math.max(prev - 0.05, 0.5);
      // Reset pan if zooming back to 100% or less
      if (newZoom <= 1) {
        setImagePan({ x: 0, y: 0 });
      }
      return Math.round(newZoom * 100) / 100;
    });
  };

  // Reset zoom and pan
  const resetZoom = (e) => {
    e.stopPropagation();
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };

  // Mouse/Touch drag handlers for panning
  const handleDragStart = (e) => {
    if (imageZoom <= 1) return; // Only allow drag when zoomed in
    e.preventDefault();
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX - imagePan.x, y: clientY - imagePan.y });
  };

  const handleDragMove = (e) => {
    if (!isDragging || imageZoom <= 1) return;
    e.preventDefault();
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    setImagePan({
      x: clientX - dragStart.x,
      y: clientY - dragStart.y
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Toggle fullscreen
  const toggleFullscreen = (e) => {
    e.stopPropagation();
    if (!document.fullscreenElement) {
      const previewElement = document.getElementById('image-preview-container');
      if (previewElement && previewElement.requestFullscreen) {
        previewElement.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
        // Reset zoom when exiting fullscreen
        setImageZoom(1);
        setImagePan({ x: 0, y: 0 });
      }
    }
  };

  // Listen for fullscreen change events
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);
      // Reset zoom when exiting fullscreen (e.g., pressing Escape)
      if (!isNowFullscreen) {
        setImageZoom(1);
        setImagePan({ x: 0, y: 0 });
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Reset zoom and pan when changing images
  React.useEffect(() => {
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  }, [previewImageIndex]);

  // Get total media count for a product
  const getMediaCount = (product) => {
    let count = product.image_url ? 1 : 0;
    if (product.media_gallery && product.media_gallery.length > 0) {
      count += product.media_gallery.length;
    }
    return count;
  };

  const handleCheckout = () => {
    setShowCart(false);
    // If no order type selected, show order type modal first
    if (!checkoutForm.orderType) {
      setShowOrderTypeModal(true);
    } else {
      setShowCheckout(true);
    }
  };

  const handleOrderTypeSelect = (orderType) => {
    const selectedType = enabledOrderTypes.find(t => t.value === orderType);
    setCheckoutForm({
      ...checkoutForm,
      orderType: orderType,
      orderTypeLabel: selectedType?.label || ''
    });
    setShowOrderTypeModal(false);
    setShowCheckout(true);
    
    // Scroll to top of checkout form after modal closes
    setTimeout(() => {
      const checkoutContent = document.querySelector('.checkout-scroll-container');
      if (checkoutContent) {
        checkoutContent.scrollTop = 0;
      }
    }, 100);
  };

  const handleCityChange = (cityName) => {
    setSelectedCity(cityName);
    const selectedArea = deliveryAreas.find(area => area.cityName === cityName);
    if (selectedArea) {
      setAvailableZipCodes(selectedArea.zipCodes || []);
      setCheckoutForm({
        ...checkoutForm,
        city: cityName,
        zipCode: '' // Reset zip code when city changes
      });
    }
  };

  const handleShippingCountryChange = (countryName) => {
    setCheckoutForm({ ...checkoutForm, shippingCountry: countryName, shippingState: '' });
    const selectedCountry = shippingCountries.find(c => c.name === countryName);
    const enabledStates = selectedCountry?.states.filter(s => s.enabled) || [];
    setAvailableStates(enabledStates);
  };

  // Apply coupon code
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setApplyingCoupon(true);
    setCouponError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/validate-coupon`, {
        code: couponCode.trim(),
        orderTotal: getCartTotal()
      });

      if (response.data.valid) {
        setAppliedCoupon(response.data.coupon);
        setCouponError('');
        setCouponCode('');
      } else {
        setCouponError(response.data.message || 'Invalid coupon code');
        setAppliedCoupon(null);
      }
    } catch (error) {
      setCouponError(error.response?.data?.message || 'Failed to apply coupon');
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  // Remove applied coupon
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  // Calculate coupon discount
  const calculateCouponDiscount = (subtotal) => {
    if (!appliedCoupon) return 0;

    if (appliedCoupon.discountType === 'percentage') {
      return subtotal * (appliedCoupon.discountValue / 100);
    } else {
      // Fixed amount discount
      return Math.min(appliedCoupon.discountValue, subtotal);
    }
  };

  // Handle Pay Now button click - open payment modal or PIN modal
  const handlePayNowClick = () => {
    // Check if this is Order at Table and PIN is enabled
    if (checkoutForm.orderType === 'orderAtTable' && ecommerceSettings?.orderAtTable?.enablePIN) {
      setShowPINModal(true);
      return;
    }
    
    // Check if payments are enabled and this is not PIN mode
    if (checkoutForm.orderType === 'orderAtTable' && ecommerceSettings?.orderAtTable?.enablePayments === false) {
      // No payment or PIN required, place order directly
      handlePlaceOrder(null);
      return;
    }
    
    if (storeSettings?.payments?.enabled) {
      setShowPaymentModal(true);
    } else {
      // If payments not enabled, place order directly
      handlePlaceOrder(null);
    }
  };

  // Handle successful payment from modal
  const handlePaymentSuccess = async (paymentMethod) => {
    await handlePlaceOrder(paymentMethod);
  };

  // Handle PIN validation
  const handlePINSubmit = async () => {
    if (!pinInput || pinInput.length !== 4) {
      setPinError('Please enter a 4-digit PIN');
      return;
    }

    setPinValidating(true);
    setPinError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/order-at-table/validate-pin`, {
        pin: pinInput
      });

      if (response.data.valid) {
        setShowPINModal(false);
        setPinInput('');
        await handlePlaceOrder(null);
      } else {
        setPinError(response.data.message || 'Invalid PIN');
      }
    } catch (error) {
      console.error('Error validating PIN:', error);
      setPinError('Failed to validate PIN. Please try again.');
    } finally {
      setPinValidating(false);
    }
  };

  const handlePlaceOrder = async (paymentMethod) => {
    setIsPlacingOrder(true);

    const subtotal = getCartTotal();
    const discount = calculateDiscount(subtotal, checkoutForm.orderType);
    const couponDiscount = calculateCouponDiscount(subtotal);
    const tax = calculateTax(subtotal);
    const tip = calculateTip(subtotal);
    const shippingFee = calculateShippingFee();
    const total = subtotal - discount - couponDiscount + tax + tip + shippingFee;

    const orderData = {
      customer: {
        name: checkoutForm.fullName,
        email: checkoutForm.email,
        phone: checkoutForm.phone
      },
      orderType: checkoutForm.orderType,
      orderTypeLabel: checkoutForm.orderTypeLabel,
      // Pickup details
      pickupLocation: (() => {
        if (checkoutForm.orderType === 'orderNowPickup' || checkoutForm.orderType === 'preOrderPickup') {
          if (!checkoutForm.pickupLocation && checkoutForm.pickupLocation !== 0) {
            return null;
          }
          const currentPickupLocations = pickupLocations[checkoutForm.orderType] || [];
          const index = parseInt(checkoutForm.pickupLocation);
          if (!isNaN(index) && currentPickupLocations[index]) {
            const location = currentPickupLocations[index];
            // Store full location info as a string
            if (location.name && location.address) {
              return `${location.name} - ${location.address}`;
            } else if (location.name) {
              return location.name;
            } else if (location.address) {
              return location.address;
            }
          }
          // Return the original value if we can't resolve it
          return checkoutForm.pickupLocation?.toString() || null;
        }
        return null;
      })(),
      pickupDate: (checkoutForm.orderType === 'preOrderPickup') ? checkoutForm.pickupDate : null,
      pickupTime: (checkoutForm.orderType === 'orderNowPickup' || checkoutForm.orderType === 'preOrderPickup') ? checkoutForm.pickupTime : null,
      // Delivery details
      deliveryAddress: (checkoutForm.orderType === 'orderNowDelivery' || checkoutForm.orderType === 'preOrderDelivery') 
        ? `${checkoutForm.streetAddress}, ${checkoutForm.city}, ${checkoutForm.zipCode}` 
        : null,
      deliveryDate: (checkoutForm.orderType === 'preOrderDelivery') ? checkoutForm.pickupDate : null,
      deliveryTime: (checkoutForm.orderType === 'orderNowDelivery' || checkoutForm.orderType === 'preOrderDelivery') ? checkoutForm.deliveryTime : null,
      deliveryInstructions: checkoutForm.deliveryInstructions || '',
      // Shipping details
      shippingAddress: checkoutForm.orderType === 'shipping' 
        ? {
            street: checkoutForm.shippingStreetAddress,
            city: checkoutForm.shippingCity,
            state: checkoutForm.shippingState,
            zipCode: checkoutForm.shippingZipCode,
            country: checkoutForm.shippingCountry
          }
        : null,
      shippingInstructions: checkoutForm.orderType === 'shipping' ? checkoutForm.shippingInstructions || '' : '',
      shippingFee: shippingFee,
      shippingOption: checkoutForm.orderType === 'shipping' && ecommerceSettings?.shipping?.shippingType === 'flat' 
        ? ecommerceSettings.shipping.flatShippingOptions[checkoutForm.selectedShippingOption]
        : null,
      // Table details for Order at Table
      assigned_table_id: checkoutForm.orderType === 'orderAtTable' ? checkoutForm.tableId : null,
      assigned_table_name: checkoutForm.orderType === 'orderAtTable' ? checkoutForm.tableName : null,
      items: cart.map(item => {
        // Debug table info
        if (checkoutForm.orderType === 'orderAtTable') {
          console.log('🍽️ Order at Table - Table Info:', {
            orderType: checkoutForm.orderType,
            tableId: checkoutForm.tableId,
            tableName: checkoutForm.tableName,
            fullCheckoutForm: checkoutForm
          });
        }

        console.log('Cart item being sent:', {
          id: item.id,
          name: item.name,
          hasVariant: !!item.variant,
          hasSelectedOptions: !!item.selectedOptions,
          hasOptions: !!item.options,
          variant: item.variant,
          selectedOptions: item.selectedOptions,
          options: item.options
        });
        return {
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          variant: item.variant || null,
          selectedOptions: item.selectedOptions || null,
          options: item.options || null
        };
      }),
      subtotal: subtotal,
      discount: discount,
      couponCode: appliedCoupon?.code || null,
      couponDiscount: couponDiscount,
      tax: tax,
      tip: tip,
      total: total,
      paymentMethodId: paymentMethod?.id || null,
      specialInstructions: checkoutForm.specialInstructions || '',
      status: 'pending'
      // orderDate will be set by backend in business timezone
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const data = await response.json();
        
        // For Order at Table, show custom confirmation modal
        if (checkoutForm.orderType === 'orderAtTable') {
          const subtotal = getCartTotal();
          const discount = calculateDiscount(subtotal, checkoutForm.orderType);
          const couponDiscount = calculateCouponDiscount ? calculateCouponDiscount(subtotal) : 0;
          const tax = calculateTax(subtotal);
          const shippingFee = calculateShippingFee ? calculateShippingFee(subtotal, checkoutForm.orderType) : 0;
          const tip = calculateTip ? calculateTip(subtotal) : 0;
          const total = (subtotal - discount - couponDiscount + tax + tip + shippingFee).toFixed(2);
          
          setConfirmedOrder({
            ...data,
            tableName: checkoutForm.tableName,
            orderTime: new Date().toLocaleString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true,
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }),
            items: cart,
            subtotal: subtotal.toFixed(2),
            discount: discount.toFixed(2),
            couponDiscount: couponDiscount.toFixed(2),
            tax: tax.toFixed(2),
            tip: tip.toFixed(2),
            shippingFee: shippingFee.toFixed(2),
            total: total,
            appliedCoupon: appliedCoupon
          });
          setShowOrderConfirmation(true);
          sessionStorage.removeItem('orderAtTable'); // Clean up
        } else {
          // Show regular success message for other order types
          setErrorMessage(`Order placed successfully! Order number: ${data.order_number || data.order_id}`);
          setShowErrorModal(true);
        }
        
        // Reset cart and form
        setCart([]);
        localStorage.removeItem('orderOnline_cart'); // Clear saved cart
        setCheckoutForm({
          fullName: '',
          email: '',
          phone: '',
          orderType: '',
          orderTypeLabel: '',
          pickupLocation: '',
          pickupDate: '',
          pickupTime: '',
          city: '',
          zipCode: '',
          streetAddress: '',
          deliveryInstructions: '',
          deliveryTime: '',
          specialInstructions: '',
          paymentMethod: 'card'
        });
        setShowCheckout(false);
        setShowPaymentModal(false);
      } else {
        // Get error details from response
        const errorText = await response.text();
        console.error('Order submission failed:', response.status, errorText);
        setErrorMessage(`Failed to place order. Please try again. (Error: ${response.status})`);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error placing order:', error);
      console.error('Error details:', error.message);
      console.error('Order data that failed:', JSON.stringify(orderData, null, 2));
      setErrorMessage('Failed to place order. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const activeCategoryName = menuCategories.find(cat => cat.id === activeMenuCategory)?.name || '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Tabs - Sticky below main nav */}
      <div className="bg-white shadow-sm sticky z-40" style={{ top: '6.5rem' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Menu Category Tabs with Filter and Cart Icons */}
          <div className="flex items-center justify-between border-b border-gray-200">
            <div className="flex space-x-1 overflow-x-auto flex-1">
              {menuCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveMenuCategory(category.id)}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeMenuCategory === category.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            
            {/* Filter and Cart Icons */}
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={() => setShowCategoryFilter(true)}
                className="relative p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Filter className="h-5 w-5" />
                {selectedProductCategory !== 'all' && (
                  <span className="absolute -top-1 -right-1 bg-gray-500 text-white text-xs font-bold rounded h-5 w-5 flex items-center justify-center">
                    •
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowCart(true)}
                className="relative p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ShoppingCart className="h-5 w-5" />
                {getCartItemCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded h-5 min-w-[1.25rem] px-1 flex items-center justify-center">
                    {getCartItemCount()}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Products Grid */}
        <div>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                <p className="mt-4 text-gray-600">Loading products...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No products available in this category</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredProducts.map(product => (
                  <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <div className="flex">
                      {/* Product Image - Larger */}
                      <div className="w-40 h-40 flex-shrink-0 bg-gray-50 relative group">
                        {product.image_url ? (
                          <>
                            <img
                              src={product.image_url}
                              alt={product.name}
                              className="w-full h-full object-contain"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openImagePreview(product, 0);
                              }}
                              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              type="button"
                            >
                              <Eye className="h-8 w-8 text-white" />
                            </button>
                            {/* Image Count Badge */}
                            {getMediaCount(product) > 1 && (
                              <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {getMediaCount(product)}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-400 text-xs">No image</span>
                          </div>
                        )}
                      </div>

                      {/* Product Info - Compact */}
                      <div className="flex-1 p-3 flex flex-col justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm mb-1">{product.name}</h3>
                          <p className="text-xs text-gray-600">{product.description}</p>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm font-semibold text-gray-600">${product.price.toFixed(2)}</span>
                          <div className="flex items-center gap-1">
                            {product.bargain_enabled && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openBargainModal(product);
                                }}
                                className="text-white px-2 py-1.5 rounded-lg hover:opacity-90 transition-colors text-xs"
                                style={{ backgroundColor: 'rgb(234, 88, 12)' }}
                                title="Make a price offer"
                              >
                                💰 Bargain
                              </button>
                            )}
                            <button
                              onClick={() => addToCart(product)}
                              disabled={addingToCart === product.id}
                              className="text-white px-3 py-1.5 rounded-lg hover:opacity-90 transition-colors flex items-center gap-1 text-sm disabled:opacity-70"
                              style={{ backgroundColor: 'var(--theme-primary)' }}
                            >
                              {addingToCart === product.id ? (
                                <>
                                  <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Adding
                                </>
                              ) : (
                                <>
                                  <Plus className="h-3 w-3" />
                                  Add
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      {/* Category Filter Dropdown Overlay */}
      {showCategoryFilter && (
        <div className="fixed inset-0 z-30" onClick={() => setShowCategoryFilter(false)}>
          {/* Centered container matching page width */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ marginTop: 'calc(6.5rem + 5px)' }}>
            <div 
              className="bg-white shadow-lg border border-gray-200 rounded-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Filter by Category</h3>
                <div className="space-y-2">
                  {/* All Categories */}
                  <button
                    onClick={() => {
                      setSelectedProductCategory('all');
                      setShowCategoryFilter(false);
                    }}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-all text-sm ${
                      selectedProductCategory === 'all'
                        ? 'bg-gray-200 text-gray-900 font-semibold'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    All Categories
                  </button>

                  {/* Individual Categories */}
                  {productCategories.length > 0 ? (
                    productCategories.map((category, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedProductCategory(category);
                          setShowCategoryFilter(false);
                        }}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-all text-sm ${
                          selectedProductCategory === category
                            ? 'bg-gray-200 text-gray-900 font-semibold'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                        }`}
                      >
                        {category}
                      </button>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 py-2">
                      No product categories available for this menu
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart Modal */}
      {showCart && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col" style={{ backgroundColor: '#2C3E50' }}>
            {/* Cart Header */}
            <div className="px-6 py-4 border-b border-gray-600 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">Cart</h2>
              <button onClick={() => setShowCart(false)} className="text-gray-300 hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {cart.length === 0 ? (
                <p className="text-center text-gray-400 py-8">Your cart is empty</p>
              ) : (
                <div className="space-y-4">
                  {cart.map(item => (
                    <div key={item.cartItemId || item.id} className="bg-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-white text-sm">{item.name}</h3>
                          
                          {/* Display selected variant */}
                          {item.variant && (
                            <p className="text-blue-300 text-xs mt-1">
                              • {item.variant.name}
                            </p>
                          )}
                          
                          {/* Display selected options */}
                          {item.selectedOptions && (() => {
                            const product = products.find(p => p.id === item.id);
                            if (!product || !product.options) return null;
                            
                            return (
                              <div className="text-xs text-gray-300 mt-1 space-y-0.5">
                                {product.options.map(option => {
                                  const selectedValue = item.selectedOptions[option.option_id];
                                  if (!selectedValue) return null;
                                  
                                  if (option.option_type === 'single') {
                                    return (
                                      <p key={option.option_id}>
                                        • {option.option_name}
                                        {option.price && ` (+$${parseFloat(option.price).toFixed(2)})`}
                                      </p>
                                    );
                                  } else if (option.option_type === 'dropdown') {
                                    const choice = option.choices?.find(c => c.choice_id === selectedValue);
                                    if (!choice) return null;
                                    return (
                                      <p key={option.option_id}>
                                        • {option.option_name}: {choice.choice_name}
                                        {choice.price && ` (+$${parseFloat(choice.price).toFixed(2)})`}
                                      </p>
                                    );
                                  }
                                  return null;
                                })}
                              </div>
                            );
                          })()}
                          
                          <p className="text-gray-300 text-sm mt-1">${item.price.toFixed(2)} each</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              if (item.cartItemId) {
                                // For customized products, update by cartItemId
                                setCart(cart.map(cartItem =>
                                  cartItem.cartItemId === item.cartItemId
                                    ? { ...cartItem, quantity: Math.max(1, cartItem.quantity - 1) }
                                    : cartItem
                                ).filter(cartItem => cartItem.quantity > 0));
                              } else {
                                updateQuantity(item.id, -1);
                              }
                            }}
                            className="bg-gray-600 text-white p-2 rounded hover:bg-gray-500"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="text-white font-medium w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => {
                              if (item.cartItemId) {
                                // For customized products, update by cartItemId
                                setCart(cart.map(cartItem =>
                                  cartItem.cartItemId === item.cartItemId
                                    ? { ...cartItem, quantity: cartItem.quantity + 1 }
                                    : cartItem
                                ));
                              } else {
                                updateQuantity(item.id, 1);
                              }
                            }}
                            className="bg-gray-600 text-white p-2 rounded hover:bg-gray-500"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            if (item.cartItemId) {
                              // For customized products, remove by cartItemId
                              setCart(cart.filter(cartItem => cartItem.cartItemId !== item.cartItemId));
                            } else {
                              removeFromCart(item.id);
                            }
                          }}
                          className="bg-red-600 text-white p-2 rounded hover:bg-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Footer */}
            {cart.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-600">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-base font-semibold text-white">Total</span>
                  <span className="text-base font-bold text-white">${getCartTotal().toFixed(2)}</span>
                </div>
                <button
                  onClick={handleCheckout}
                  className="w-full bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-500 transition-colors font-medium"
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Checkout Modal */}
      {showCheckout && stripeLoaded && ReactDOM.createPortal(
        <Elements stripe={stripePromise}>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col" style={{ backgroundColor: '#2C3E50' }}>
              {/* Checkout Header */}
              <div className="px-5 py-3 border-b border-gray-600 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">Checkout</h2>
                  <div className="flex items-center gap-3">
                    {checkoutForm.orderType && (
                      <button
                        onClick={() => {
                          if (checkoutForm.orderType !== 'orderAtTable') {
                            setShowOrderTypeModal(true);
                          }
                        }}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-colors ${
                          checkoutForm.orderType === 'orderAtTable'
                            ? 'bg-gray-700 cursor-not-allowed opacity-60'
                            : 'bg-gray-700 hover:bg-gray-600 group cursor-pointer'
                        }`}
                        title={checkoutForm.orderType === 'orderAtTable' ? 'Order type cannot be changed' : 'Change order type'}
                        disabled={checkoutForm.orderType === 'orderAtTable'}
                      >
                        <Settings className={`h-4 w-4 transition-colors ${
                          checkoutForm.orderType === 'orderAtTable'
                            ? 'text-gray-500'
                            : 'text-gray-400 group-hover:text-[rgb(41,173,211)]'
                        }`} />
                        <span className="text-xs text-gray-300">{checkoutForm.orderTypeLabel}</span>
                      </button>
                    )}
                    <button onClick={() => setShowCheckout(false)} className="text-gray-300 hover:text-white">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              <div 
                className="px-5 py-4 space-y-4 overflow-y-auto flex-1 checkout-scroll checkout-scroll-container"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#4B5563 #374151'
                }}
              >
                <style>{`
                  .checkout-scroll::-webkit-scrollbar {
                    width: 6px;
                  }
                  .checkout-scroll::-webkit-scrollbar-track {
                    background: #374151;
                  }
                  .checkout-scroll::-webkit-scrollbar-thumb {
                    background: #4B5563;
                    border-radius: 3px;
                  }
                  .checkout-scroll::-webkit-scrollbar-thumb:hover {
                    background: #6B7280;
                  }
                  
                  /* Fix autofill white background in dark theme */
                  input:-webkit-autofill,
                  input:-webkit-autofill:hover,
                  input:-webkit-autofill:focus,
                  input:-webkit-autofill:active,
                  textarea:-webkit-autofill,
                  textarea:-webkit-autofill:hover,
                  textarea:-webkit-autofill:focus,
                  textarea:-webkit-autofill:active {
                    -webkit-box-shadow: 0 0 0 30px #374151 inset !important;
                    -webkit-text-fill-color: white !important;
                    caret-color: white !important;
                  }
                `}</style>
                <CheckoutFormContent
                  checkoutForm={checkoutForm}
                  setCheckoutForm={setCheckoutForm}
                  enabledOrderTypes={enabledOrderTypes}
                  handlePlaceOrder={handlePlaceOrder}
                  getCartTotal={getCartTotal}
                  calculateTax={calculateTax}
                  calculateDiscount={calculateDiscount}
                  calculateCouponDiscount={calculateCouponDiscount}
                  calculateShippingFee={calculateShippingFee}
                  appliedCoupon={appliedCoupon}
                  cart={cart}
                  cardComplete={cardComplete}
                  setCardComplete={setCardComplete}
                  isPlacingOrder={isPlacingOrder}
                  storeSettings={storeSettings}
                  ecommerceSettings={ecommerceSettings}
                  setShowOrderTypeModal={setShowOrderTypeModal}
                  deliveryAreas={deliveryAreas}
                  handleCityChange={handleCityChange}
                  availableZipCodes={availableZipCodes}
                  pickupLocations={pickupLocations}
                  availablePickupTimes={calculateAvailablePickupTimes()}
                  availableDeliveryTimes={calculateAvailableDeliveryTimes()}
                  calculatePreOrderPickupDates={calculatePreOrderPickupDates}
                  calculatePreOrderPickupTimes={calculatePreOrderPickupTimes}
                  calculatePreOrderDeliveryDates={calculatePreOrderDeliveryDates}
                  calculatePreOrderDeliveryTimeRanges={calculatePreOrderDeliveryTimeRanges}
                  showCouponInput={showCouponInput}
                  setShowCouponInput={setShowCouponInput}
                  couponCode={couponCode}
                  setCouponCode={setCouponCode}
                  couponError={couponError}
                  applyingCoupon={applyingCoupon}
                  handleApplyCoupon={handleApplyCoupon}
                  handleRemoveCoupon={handleRemoveCoupon}
                  shippingCountries={shippingCountries}
                  handleShippingCountryChange={handleShippingCountryChange}
                  availableStates={availableStates}
                  businessInfo={businessInfo}
                  selectedTipOption={selectedTipOption}
                  setSelectedTipOption={setSelectedTipOption}
                  customTipAmount={customTipAmount}
                  setCustomTipAmount={setCustomTipAmount}
                  calculateTip={calculateTip}
                />
              </div>

              {/* Sticky Footer with Pay Now Button */}
              <CheckoutFooter
                checkoutForm={checkoutForm}
                isPlacingOrder={isPlacingOrder}
                getCartTotal={getCartTotal}
                calculateTax={calculateTax}
                calculateDiscount={calculateDiscount}
                calculateCouponDiscount={calculateCouponDiscount}
                calculateShippingFee={calculateShippingFee}
                calculateTip={calculateTip}
                appliedCoupon={appliedCoupon}
                handlePlaceOrder={handlePlaceOrder}
                storeSettings={storeSettings}
                cardComplete={cardComplete}
                pickupLocations={pickupLocations}
                onPayNowClick={handlePayNowClick}
                ecommerceSettings={ecommerceSettings}
              />
            </div>
          </div>
        </Elements>,
        document.body
      )}

      {/* Order Type Selection Modal */}
      {showOrderTypeModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-3xl w-full shadow-2xl" style={{ backgroundColor: '#2C3E50' }}>
            {/* Modal Header */}
            <div className="px-5 py-3 border-b border-gray-600 flex items-center justify-between">
              <h3 className="text-base font-semibold text-white">Select Order Type</h3>
              <button 
                onClick={() => setShowOrderTypeModal(false)} 
                className="text-gray-300 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-5 py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {enabledOrderTypes.map((orderType) => {
                  const isPreOrder = orderType.value.includes('preOrder');
                  const isOrderNow = orderType.value.includes('orderNow');
                  const storeStatus = isOrderNow ? getStoreStatus(orderType.value) : { isAvailable: true, isClosed: false, isClosingSoon: false, warningClosingSoon: false };
                  const isUnavailable = isOrderNow && !storeStatus.isAvailable;
                  const showWarning = isOrderNow && storeStatus.warningClosingSoon;
                  const discountEnabled = isPreOrder && (
                    (orderType.value === 'preOrderPickup' && ecommerceSettings?.preOrderPickup?.discountPercentage > 0) ||
                    (orderType.value === 'preOrderDelivery' && ecommerceSettings?.preOrderDelivery?.discountPercentage > 0)
                  );
                  const discountPercent = orderType.value === 'preOrderPickup' 
                    ? ecommerceSettings?.preOrderPickup?.discountPercentage 
                    : ecommerceSettings?.preOrderDelivery?.discountPercentage;

                  return (
                    <button
                      key={orderType.value}
                      onClick={() => !isUnavailable && handleOrderTypeSelect(orderType.value)}
                      disabled={isUnavailable}
                      className={`rounded-lg p-4 text-left transition-all group relative ${
                        isUnavailable 
                          ? 'bg-gray-800 border-2 border-gray-700 cursor-not-allowed opacity-60' 
                          : showWarning
                            ? 'bg-gray-700 hover:bg-gray-600 border-2 border-yellow-600 hover:border-yellow-500'
                            : 'bg-gray-700 hover:bg-gray-600 border-2 border-gray-600 hover:border-[rgb(41,173,211)]'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          {orderType.value.includes('Pickup') ? (
                            <ShoppingCart className={`h-7 w-7 ${isUnavailable ? 'text-gray-500' : 'text-[rgb(41,173,211)]'}`} />
                          ) : (
                            <svg className={`h-7 w-7 ${isUnavailable ? 'text-gray-500' : 'text-[rgb(41,173,211)]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 pr-6">
                          <h4 className={`text-sm font-semibold mb-1 transition-colors ${
                            isUnavailable 
                              ? 'text-gray-500' 
                              : 'text-white group-hover:text-[rgb(41,173,211)]'
                          }`}>
                            {orderType.label}
                          </h4>
                          {isUnavailable ? (
                            <div>
                              <p className="text-xs text-red-400 font-medium mb-1">
                                🔒 {storeStatus.isClosed ? 'Store Closed Today' : 'Closed for Order Now'}
                              </p>
                              <p className="text-xs text-gray-500 leading-relaxed">
                                {storeStatus.isClosed 
                                  ? 'Store is closed. Please try Pre-Order for a future date.' 
                                  : 'Store closing soon. Please try Pre-Order instead.'}
                              </p>
                            </div>
                          ) : showWarning ? (
                            <div>
                              <p className="text-xs text-yellow-400 leading-relaxed">
                                ⚠️ Store closing within 1 hour. Limited time available.
                              </p>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 leading-relaxed">
                              {orderType.value === 'orderNowPickup' && 'Pick up as soon as ready'}
                              {orderType.value === 'orderNowDelivery' && 'Get delivered to your address'}
                              {orderType.value === 'preOrderPickup' && 'Schedule pickup for later'}
                              {orderType.value === 'preOrderDelivery' && 'Schedule delivery for later'}
                            </p>
                          )}
                          {discountEnabled && !isUnavailable && (
                            <p className="text-xs text-green-400 mt-1 font-medium">
                              Save {discountPercent}% on pre-orders!
                            </p>
                          )}
                        </div>
                        {!isUnavailable && (
                          <svg className="h-5 w-5 text-gray-400 group-hover:text-[rgb(41,173,211)] transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Validation Error Modal */}
      {showValidationModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl" style={{ backgroundColor: '#2C3E50' }}>
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-600 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <svg className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Missing Required Fields
              </h3>
              <button 
                onClick={() => setShowValidationModal(false)} 
                className="text-gray-300 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6">
              <p className="text-gray-300 text-sm mb-4">
                Please complete the following required fields to continue:
              </p>
              <ul className="space-y-2">
                {validationErrors.map((error, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
                    <svg className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-600">
              <button
                onClick={() => setShowValidationModal(false)}
                className="w-full py-3 rounded-lg font-medium text-white transition-colors"
                style={{ backgroundColor: 'var(--theme-primary)' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-primary)'}
              >
                OK, Got It
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Product Customization Modal */}
      {showCustomizeModal && customizingProduct && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex-shrink-0 px-4 py-3 flex items-center justify-between" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h3 className="text-lg font-semibold text-white">{customizingProduct.name}</h3>
              <button
                onClick={() => {
                  setShowCustomizeModal(false);
                  setCustomizingProduct(null);
                  setSelectedVariant(null);
                  setSelectedOptions({});
                  setCustomizeQuantity(1);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content - Scrollable with custom scrollbar */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 custom-scrollbar" style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'var(--theme-primary) transparent'
            }}>
              {/* Product Image and Description */}
              <div className="flex gap-3">
                {customizingProduct.image_url && (
                  <div className="relative w-40 h-40 flex-shrink-0 bg-gray-50 rounded-lg group">
                    <img
                      src={customizingProduct.image_url}
                      alt={customizingProduct.name}
                      className="w-full h-full object-contain rounded-lg"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openImagePreview(customizingProduct, 0);
                      }}
                      className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                      type="button"
                    >
                      <Eye className="h-8 w-8 text-white" />
                    </button>
                    {/* Image Count Badge */}
                    {getMediaCount(customizingProduct) > 1 && (
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {getMediaCount(customizingProduct)}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {customizingProduct.description && (
                    <p className="text-sm text-gray-600 mb-1 line-clamp-3">{customizingProduct.description}</p>
                  )}
                  <p className="text-base font-semibold text-gray-900">
                    ${(() => {
                      // Show variant price if selected, otherwise base price
                      if (customizingProduct.is_variable && selectedVariant) {
                        return parseFloat(selectedVariant.price).toFixed(2);
                      }
                      return customizingProduct.price.toFixed(2);
                    })()}
                  </p>
                </div>
              </div>

              {/* Variant Selection */}
              {customizingProduct.is_variable && customizingProduct.variants && customizingProduct.variants.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-2.5 bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    Select {customizingProduct.variation_type || 'Variant'} *
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {customizingProduct.variants.map((variant, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedVariant(variant)}
                        className={`p-2 rounded-md border-2 transition-all text-left ${
                          selectedVariant?.name === variant.name
                            ? 'border-[rgb(41,173,211)] bg-blue-50'
                            : 'border-gray-300 bg-white hover:border-gray-400'
                        }`}
                      >
                        <div className="text-sm font-medium text-gray-900">{variant.name}</div>
                        <div className="text-sm text-gray-600">${parseFloat(variant.price).toFixed(2)}</div>
                        {variant.stock !== undefined && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {variant.stock > 0 ? `${variant.stock} in stock` : 'Out of stock'}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Options Selection */}
              {customizingProduct.has_options && customizingProduct.options && customizingProduct.options.length > 0 && (
                <div className="space-y-2.5">
                  {customizingProduct.options.map((option) => (
                    <div key={option.option_id} className="border border-gray-200 rounded-lg p-2.5 bg-gray-50">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">
                        {option.option_name} {option.is_required && <span className="text-red-500">*</span>}
                      </h4>

                      {/* Single Option (Checkbox) */}
                      {option.option_type === 'single' && (
                        <label className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded-md border border-gray-300 hover:border-gray-400 transition-all">
                          <input
                            type="checkbox"
                            checked={selectedOptions[option.option_id] === true}
                            onChange={(e) => {
                              const newOptions = { ...selectedOptions };
                              if (e.target.checked) {
                                newOptions[option.option_id] = true;
                              } else {
                                delete newOptions[option.option_id];
                              }
                              setSelectedOptions(newOptions);
                            }}
                            className="w-4 h-4 text-[rgb(41,173,211)] rounded focus:ring-1 focus:ring-[rgb(41,173,211)]"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-900">Add {option.option_name}</span>
                            {option.price && (
                              <span className="text-sm text-gray-600 ml-1.5">+${parseFloat(option.price).toFixed(2)}</span>
                            )}
                          </div>
                        </label>
                      )}

                      {/* Dropdown Option */}
                      {option.option_type === 'dropdown' && option.choices && option.choices.length > 0 && (
                        <div className="space-y-1.5">
                          {option.choices.map((choice) => (
                            <label
                              key={choice.choice_id}
                              className={`flex items-center gap-2 cursor-pointer p-2 rounded-md border-2 transition-all ${
                                selectedOptions[option.option_id] === choice.choice_id
                                  ? 'border-[rgb(41,173,211)] bg-blue-50'
                                  : 'border-gray-300 bg-white hover:border-gray-400'
                              }`}
                            >
                              <input
                                type="radio"
                                name={option.option_id}
                                checked={selectedOptions[option.option_id] === choice.choice_id}
                                onChange={() => {
                                  setSelectedOptions({
                                    ...selectedOptions,
                                    [option.option_id]: choice.choice_id
                                  });
                                }}
                                className="w-4 h-4 text-[rgb(41,173,211)] focus:ring-1 focus:ring-[rgb(41,173,211)]"
                              />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-900">{choice.choice_name}</span>
                                {choice.price && (
                                  <span className="text-sm text-gray-600 ml-1.5">+${parseFloat(choice.price).toFixed(2)}</span>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer with Quantity */}
            <div className="flex-shrink-0 bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
              {/* Quantity Selector */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCustomizeQuantity(Math.max(1, customizeQuantity - 1))}
                  className="p-1.5 rounded-md bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  <Minus className="h-4 w-4 text-gray-700" />
                </button>
                <span className="text-base font-semibold text-gray-900 min-w-[35px] text-center">
                  {customizeQuantity}
                </span>
                <button
                  onClick={() => setCustomizeQuantity(customizeQuantity + 1)}
                  className="p-1.5 rounded-md bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  <Plus className="h-4 w-4 text-gray-700" />
                </button>
              </div>
              
              {/* Total and Add to Cart */}
              <div className="flex items-center gap-3">
                <div className="text-base font-bold text-gray-900">
                  ${(() => {
                  let total = customizingProduct.price;
                  
                  // Calculate variant price
                  if (customizingProduct.is_variable && selectedVariant) {
                    total = parseFloat(selectedVariant.price) || 0;
                  }
                  
                  // Calculate options price
                  if (customizingProduct.has_options && customizingProduct.options) {
                    customizingProduct.options.forEach(option => {
                      const selectedValue = selectedOptions[option.option_id];
                      
                      if (option.option_type === 'single' && selectedValue) {
                        total += parseFloat(option.price) || 0;
                      } else if (option.option_type === 'dropdown' && selectedValue) {
                        const choice = option.choices?.find(c => c.choice_id === selectedValue);
                        if (choice && choice.price) {
                          total += parseFloat(choice.price) || 0;
                        }
                      }
                    });
                  }
                  
                  return (total * customizeQuantity).toFixed(2);
                })()}
                </div>
                <div className="flex gap-2">
                  {customizingProduct?.bargain_enabled && (
                    <button
                      onClick={() => {
                        setShowProductModal(false);
                        openBargainModal(customizingProduct);
                      }}
                      className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-colors flex items-center gap-1.5"
                      style={{ backgroundColor: 'rgb(234, 88, 12)' }}
                    >
                      💰 Bargain Price
                    </button>
                  )}
                  <button
                    onClick={() => {
                      // Validate required options
                      if (customizingProduct.has_options && customizingProduct.options) {
                        const missingRequired = customizingProduct.options.find(
                          opt => opt.is_required && !selectedOptions[opt.option_id]
                        );
                        
                        if (missingRequired) {
                          alert(`Please select ${missingRequired.option_name}`);
                          return;
                        }
                      }
                      
                      addCustomizedProductToCart();
                    }}
                    className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-colors flex items-center gap-1.5"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Custom Scrollbar Styles */}
          <style jsx>{`
            .custom-scrollbar::-webkit-scrollbar {
              width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: transparent;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: var(--theme-primary);
              border-radius: 3px;
              opacity: 0;
              transition: opacity 0.3s;
            }
            .custom-scrollbar:hover::-webkit-scrollbar-thumb {
              opacity: 1;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: var(--theme-primary-hover);
            }
          `}</style>
        </div>,
        document.body
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <Elements stripe={stripePromise}>
          <PaymentModal
            show={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            amount={getCartTotal() - calculateDiscount(getCartTotal(), checkoutForm.orderType) + calculateTax(getCartTotal()) + calculateShippingFee()}
            onPaymentSuccess={handlePaymentSuccess}
            checkoutForm={checkoutForm}
          />
        </Elements>
      )}

      {/* PIN Modal */}
      {showPINModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Enter PIN
                </h3>
                <p className="text-sm text-gray-400 mt-1">Please enter the 4-digit PIN provided by your waiter</p>
              </div>
              <button
                onClick={() => {
                  setShowPINModal(false);
                  setPinInput('');
                  setPinError('');
                }}
                disabled={pinValidating}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-750 p-4 rounded-lg border border-gray-600">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  PIN Code *
                </label>
                <input
                  type="text"
                  maxLength="4"
                  value={pinInput}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setPinInput(value);
                    setPinError('');
                  }}
                  placeholder="Enter 4-digit PIN"
                  className="w-full px-4 py-2 text-center text-lg font-semibold tracking-wider bg-gray-700 text-white border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                  autoFocus
                />
              </div>

              {pinError && (
                <div className="bg-red-900 bg-opacity-20 border border-red-500 rounded-lg p-3">
                  <p className="text-sm text-red-400">{pinError}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPINModal(false);
                    setPinInput('');
                    setPinError('');
                  }}
                  disabled={pinValidating}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePINSubmit}
                  disabled={pinValidating || pinInput.length !== 4}
                  className="flex-1 px-4 py-2 bg-[rgb(41,173,211)] text-white rounded-md hover:bg-[rgb(31,163,201)] font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {pinValidating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Validating...</span>
                    </>
                  ) : (
                    <span>Submit</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Error/Success Modal */}
      {showErrorModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="text-center">
              {errorMessage.includes('successfully') ? (
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              ) : (
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
              )}
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {errorMessage.includes('successfully') ? 'Order Placed!' : 'Order Failed'}
              </h3>
              <p className="text-gray-600 mb-6">
                {errorMessage}
              </p>
              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full px-4 py-2.5 text-white font-semibold rounded-lg hover:opacity-90 transition-colors"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Image/Video Preview Modal with Slider */}
      {showImagePreview && ReactDOM.createPortal(
        <div 
          id="image-preview-container"
          className={`fixed inset-0 bg-black ${isFullscreen ? '' : 'bg-opacity-90'} flex items-center justify-center z-50`}
          onClick={() => {
            setShowImagePreview(false);
            setImageZoom(1);
            setImagePan({ x: 0, y: 0 });
          }}
        >
          {/* Main Container - Regular modal or Fullscreen */}
          <div 
            className={`relative flex items-center justify-center ${
              isFullscreen 
                ? 'w-screen h-screen' 
                : 'max-w-[90vw] max-h-[85vh] rounded-lg'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Render Image or Video based on current media type */}
            {(() => {
              const currentMedia = previewImages[previewImageIndex];
              const isVideo = currentMedia?.type === 'video';
              const url = previewImageUrl;
              
              if (isVideo && isYouTubeUrl(url)) {
                // YouTube Video - Embedded Player
                const videoId = getYouTubeVideoId(url);
                return (
                  <div className={`${isFullscreen ? 'w-[95vw] h-[95vh]' : 'w-[85vw] max-w-4xl'} aspect-video bg-black rounded-lg overflow-hidden`}>
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                      title="YouTube video"
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                );
              } else if (isVideo || isVideoFile(url)) {
                // Local/S3 Video - HTML5 Video Player
                return (
                  <div className={`${isFullscreen ? 'w-[95vw] h-[95vh]' : 'w-[85vw] max-w-4xl'} aspect-video bg-black rounded-lg overflow-hidden`}>
                    <video
                      src={url}
                      className="w-full h-full"
                      controls
                      autoPlay
                      playsInline
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                );
              } else {
                // Image with zoom and scroll support
                const containerWidth = isFullscreen ? '98vw' : '85vw';
                const containerHeight = isFullscreen ? '98vh' : '80vh';
                
                return (
                  <div 
                    className="overflow-auto"
                    style={{ 
                      maxWidth: containerWidth,
                      maxHeight: containerHeight,
                      cursor: imageZoom > 1 ? 'move' : 'default'
                    }}
                  >
                    <img
                      src={url}
                      alt="Preview"
                      className="select-none block"
                      style={{ 
                        width: imageZoom > 1 ? `calc(${isFullscreen ? '98vw' : '85vw'} * ${imageZoom})` : 'auto',
                        height: imageZoom > 1 ? `calc(${isFullscreen ? '98vh' : '80vh'} * ${imageZoom})` : 'auto',
                        maxWidth: imageZoom > 1 ? 'none' : (isFullscreen ? '98vw' : '85vw'),
                        maxHeight: imageZoom > 1 ? 'none' : (isFullscreen ? '98vh' : '80vh'),
                        objectFit: 'contain'
                      }}
                      draggable={false}
                    />
                  </div>
                );
              }
            })()}
            
            {/* Top Right Controls - Zoom, Fullscreen, Close (Compact) */}
            <div className="absolute top-2 right-2 flex items-center gap-1 z-20">
              {/* Zoom Controls - Only for images */}
              {previewImages[previewImageIndex]?.type !== 'video' && (
                <div className="flex items-center bg-black bg-opacity-50 rounded-full px-1 py-0.5">
                  <button
                    onClick={zoomOut}
                    disabled={imageZoom <= 0.5}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    type="button"
                    title="Zoom Out"
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <button
                    onClick={resetZoom}
                    className="px-1.5 py-0.5 text-white text-xs font-medium hover:bg-white hover:bg-opacity-20 rounded transition-all min-w-[36px]"
                    type="button"
                    title="Reset Zoom"
                  >
                    {Math.round(imageZoom * 100)}%
                  </button>
                  <button
                    onClick={zoomIn}
                    disabled={imageZoom >= 3}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    type="button"
                    title="Zoom In"
                  >
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              )}
              
              {/* Fullscreen Button */}
              <button
                onClick={toggleFullscreen}
                className="p-1.5 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all"
                type="button"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  </svg>
                )}
              </button>
              
              {/* Close Button */}
              <button
                onClick={() => {
                  if (isFullscreen && document.exitFullscreen) {
                    document.exitFullscreen();
                  }
                  setShowImagePreview(false);
                  setImageZoom(1);
                  setImagePan({ x: 0, y: 0 });
                }}
                className="p-1.5 bg-black bg-opacity-50 rounded-full hover:bg-opacity-70 transition-all"
                type="button"
                title="Close"
              >
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
            
            {/* Video Badge - Top Left (Compact) */}
            {previewImages[previewImageIndex]?.type === 'video' && (
              <div className="absolute top-2 left-2 px-2 py-1 bg-purple-600 bg-opacity-70 rounded-full z-20">
                <span className="text-white text-xs font-medium">Video</span>
              </div>
            )}
            
            {/* Bottom Controls - Arrows and Counter */}
            {previewImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
                {/* Left Arrow */}
                <button
                  onClick={prevPreviewImage}
                  className="p-1.5 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-all"
                  type="button"
                  title="Previous"
                >
                  <ChevronLeft className="h-5 w-5 text-white" />
                </button>
                
                {/* Counter */}
                <div className="bg-black bg-opacity-50 rounded-full px-3 py-1">
                  <span className="text-white text-xs font-medium">
                    {previewImageIndex + 1} / {previewImages.length}
                  </span>
                </div>
                
                {/* Right Arrow */}
                <button
                  onClick={nextPreviewImage}
                  className="p-1.5 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-all"
                  type="button"
                  title="Next"
                >
                  <ChevronRight className="h-5 w-5 text-white" />
                </button>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Order at Table Confirmation Modal */}
      {showOrderConfirmation && confirmedOrder && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="px-6 py-3 rounded-t-xl flex-shrink-0 sticky top-0 z-10" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h2 className="text-lg font-semibold text-white">Order</h2>
              <p className="text-white text-xs mt-1">
                #{confirmedOrder.order_number || confirmedOrder.order_id}
              </p>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Success Message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-green-800 font-medium mb-2">
                  Your order is being prepared now!
                </p>
                <p className="text-green-700 text-sm">
                  Your food will be brought to <span className="font-bold">Table {confirmedOrder.tableName}</span> soon.
                </p>
              </div>

              {/* Order Details */}
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Order Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Table:</span>
                      <span className="font-semibold text-gray-900">{confirmedOrder.tableName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time:</span>
                      <span className="font-medium text-gray-900">{confirmedOrder.orderTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order Type:</span>
                      <span className="font-medium text-gray-900">{checkoutForm.orderTypeLabel || 'Order at Table'}</span>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Order Summary</h3>
                  <div className="space-y-2">
                    {confirmedOrder.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-700">
                          {item.quantity}x {item.name}
                        </span>
                        <span className="font-medium text-gray-900">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    
                    {/* Subtotal */}
                    <div className="border-t border-gray-300 pt-2 mt-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span className="text-gray-900">${confirmedOrder.subtotal}</span>
                      </div>
                    </div>

                    {/* Discount */}
                    {parseFloat(confirmedOrder.discount) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Discount</span>
                        <span className="text-green-600">-${confirmedOrder.discount}</span>
                      </div>
                    )}

                    {/* Coupon Discount */}
                    {parseFloat(confirmedOrder.couponDiscount) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Coupon {confirmedOrder.appliedCoupon ? `(${confirmedOrder.appliedCoupon.code})` : ''}</span>
                        <span className="text-green-600">-${confirmedOrder.couponDiscount}</span>
                      </div>
                    )}

                    {/* Tax */}
                    {parseFloat(confirmedOrder.tax) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax</span>
                        <span className="text-gray-900">${confirmedOrder.tax}</span>
                      </div>
                    )}

                    {/* Tip */}
                    {parseFloat(confirmedOrder.tip) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tip</span>
                        <span className="text-green-600">${confirmedOrder.tip}</span>
                      </div>
                    )}

                    {/* Shipping Fee */}
                    {parseFloat(confirmedOrder.shippingFee) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Shipping Fee</span>
                        <span className="text-gray-900">${confirmedOrder.shippingFee}</span>
                      </div>
                    )}

                    {/* Total */}
                    <div className="border-t border-gray-400 pt-2 mt-2">
                      <div className="flex justify-between font-bold text-gray-900">
                        <span>Total</span>
                        <span>${confirmedOrder.total}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Thank You Message */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 text-center">
                    <strong>Thank you for your order!</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 sticky bottom-0">
              <button
                onClick={() => {
                  setShowOrderConfirmation(false);
                  setConfirmedOrder(null);
                  setShowCheckout(false);
                  // Stay on Order Online page without reload
                }}
                className="w-full px-4 py-3 bg-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-400 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bargain Modal - Dark Theme */}
      {showBargainModal && bargainProduct && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-2xl shadow-2xl w-full max-h-[90vh] flex flex-col" style={{ maxWidth: '458px' }}>
            {/* Sticky Header */}
            <div className="px-6 py-4 border-b border-gray-700 flex-shrink-0 bg-gray-800 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white">Make a Price Offer</h3>
                <button 
                  onClick={closeBargainModal}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {bargainSuccess ? (
              // Success State
              <div className="p-6 text-center flex-1">
                <div className="w-16 h-16 bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-10 w-10 text-green-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Request Submitted!</h4>
                <p className="text-gray-400 mb-6">
                  We've received your price offer. If we can accommodate your request, we'll contact you via email or phone.
                </p>
                <button
                  onClick={closeBargainModal}
                  className="px-6 py-2 rounded-lg text-white font-semibold bg-gray-700 hover:bg-gray-600"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <>
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {/* Product Info */}
                  <div className="flex items-center gap-3 bg-gray-800 p-3 rounded-lg">
                    {bargainProduct.image_url ? (
                      <img 
                        src={bargainProduct.image_url} 
                        alt={bargainProduct.name}
                        className="w-16 h-16 object-contain rounded-lg bg-gray-700"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center">
                        <span className="text-gray-500 text-xs">No image</span>
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-white">{bargainProduct.name}</h4>
                      <p className="text-sm text-gray-400">
                        Listed Price: <span className="font-semibold text-cyan-400">${bargainProduct.price.toFixed(2)}</span>
                      </p>
                    </div>
                  </div>

                  {/* Your Offer */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Your Price Offer *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={bargainForm.customerQuote}
                        onChange={(e) => setBargainForm({ ...bargainForm, customerQuote: e.target.value })}
                        placeholder="Enter your offer"
                        className="w-full pl-8 pr-4 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={bargainForm.quantity}
                      onChange={(e) => setBargainForm({ ...bargainForm, quantity: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>

                  {/* First Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={bargainForm.firstName}
                      onChange={(e) => setBargainForm({ ...bargainForm, firstName: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={bargainForm.lastName}
                      onChange={(e) => setBargainForm({ ...bargainForm, lastName: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
                    <input
                      type="email"
                      value={bargainForm.email}
                      onChange={(e) => setBargainForm({ ...bargainForm, email: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={bargainForm.phone}
                      onChange={(e) => setBargainForm({ ...bargainForm, phone: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>

                  {/* Address Section (Optional) */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-300">Address (Optional)</label>
                    
                    <input
                      type="text"
                      value={bargainForm.streetAddress}
                      onChange={(e) => setBargainForm({ ...bargainForm, streetAddress: e.target.value })}
                      placeholder="Street Address"
                      className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                    
                    <input
                      type="text"
                      value={bargainForm.city}
                      onChange={(e) => setBargainForm({ ...bargainForm, city: e.target.value })}
                      placeholder="City"
                      className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                    
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={bargainForm.state}
                        onChange={(e) => setBargainForm({ ...bargainForm, state: e.target.value })}
                        placeholder="State"
                        className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={bargainForm.zipCode}
                        onChange={(e) => setBargainForm({ ...bargainForm, zipCode: e.target.value })}
                        placeholder="Zip Code"
                        className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Notes (Optional)
                      <span className="text-xs text-gray-500 ml-2">Max 200 words</span>
                    </label>
                    <textarea
                      value={bargainForm.notes}
                      onChange={(e) => {
                        const words = e.target.value.trim().split(/\s+/).filter(w => w.length > 0);
                        if (words.length <= 200 || e.target.value.length < bargainForm.notes.length) {
                          setBargainForm({ ...bargainForm, notes: e.target.value });
                        }
                      }}
                      placeholder="Any additional comments or information..."
                      rows={3}
                      className="w-full px-3 py-2 bg-gray-800 text-white border border-gray-600 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {bargainForm.notes.trim().split(/\s+/).filter(w => w.length > 0).length}/200 words
                    </p>
                  </div>

                  {/* How it works */}
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                    <h5 className="text-sm font-semibold text-cyan-400 mb-2">How it works:</h5>
                    <ul className="text-xs text-gray-400 space-y-1">
                      <li>• We'll review your offer and contact you if we can accommodate it</li>
                      <li>• If approved, you'll receive a special purchase link via email</li>
                      <li>• Very low offers may not receive a response</li>
                    </ul>
                  </div>

                  {/* Error Message */}
                  {bargainError && (
                    <div className="bg-red-900/50 border border-red-700 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-300">{bargainError}</p>
                    </div>
                  )}
                </div>

                {/* Sticky Footer */}
                <div className="px-6 py-4 border-t border-gray-700 flex-shrink-0 bg-gray-800 rounded-b-2xl">
                  <button
                    onClick={submitBargainRequest}
                    disabled={bargainSubmitting}
                    className="w-full py-3 rounded-lg text-white font-semibold bg-gray-700 hover:bg-gray-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {bargainSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      'Submit Offer'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OrderOnline;
